/**
 * properties-editor knob fields helpers util
 *
 * NOTE: methods grouped & alpha-sorted by return type; groups first
 */
import { inject } from '@angular/core';
import { Store } from '@ngrx/store';
import {
  combineLatestWith,
  distinctUntilChanged,
  map,
  type Observable,
  of,
  switchMap,
  take,
} from 'rxjs';
import { type SetOptional } from 'type-fest';

import { runtimeSelectors } from '@blaze-runtime/engine-data-access';
import {
  type CanvasElementId,
  type CanvasElementType,
  type HeightSettings,
  type PropsOf,
  type SlotIdOf,
  type WidthSettings,
} from '@blaze-runtime/engine-domain';
import { resolveElementMetaDisplayValue } from '@blaze-runtime/engine-domain';
import {
  READ_EXCLUSIVE_SLOT_PARENT_PATH_PREFIX,
  type READ_SLOT_PARENT_PATH_PREFIX,
} from '@blaze-runtime/engine-util';
import { type ColumnDataType, ComparisonOperator } from '@blaze-shared/api-contracts-domain';
import { type PredefinedDateTimeOperator } from '@blaze-shared/datasets-domain';
import {
  type ActionKind,
  type CalendarDateSelectorState,
  type CanvasElementDataProperties,
  type CanvasElementInputBinding,
  type ChartAggregateFunctionType,
  type ConditionalFieldParams,
  createAliasForAggregation,
  CURRENCY_OPTIONS,
  type DataBindingSourceInfo,
  DATASET_FORMAT_TO_TABLE_FORMAT,
  type DataSourceSelectMode,
  DEFAULT_COLUMN_TAGS_PALETTE,
  DETAILS_VIEW_CUSTOM_VALUE,
  type DisableWhenFn,
  DURATION_FORMAT_OPTIONS,
  type ElementAlignment,
  type ElementMetaUiFeaturesProps,
  type ElementRedirect,
  type ElementSizingMode,
  FILTERS_DATE_AND_TIME_PREDEFINED_OPERATOR_OPTIONS,
  FILTERS_DATE_OPERATOR_OPTIONS,
  FILTERS_DATETIME_OPERATOR_OPTIONS,
  FILTERS_NUMBER_OPERATORS,
  FONT_STYLE_OPTIONS,
  type GenericInputOptions,
  getActiveBinding,
  getDefaultVisualEffectDataForType,
  getDirectionValueMapFromCssProperty,
  getFormatByDataType,
  type HideFieldWhenFn,
  type IconSelectOption,
  IMAGE_SHAPE_OPTIONS,
  type ImageBehavior,
  type ImageShape,
  ISearchAndFiltersWrapperService,
  ITEMS_CONTAINER_ACTION_EDIT,
  ITEMS_CONTAINER_ACTION_ROW_CLICK,
  ITEMS_CONTAINER_ACTION_VIEW,
  type LabelIconAlignment,
  type LabelPosition,
  type LabelStyleSubsectionOptions,
  type LabelStyleSubsectionPaths,
  type LinkAction,
  LIST_COLUMN_FORMATS,
  NavMenu,
  PALETTE_OPTIONS,
  PRECISION_OPTIONS,
  type PropertiesConfig,
  type PropertiesConfigImagePropertiesModel,
  type PropertiesConfigInputColorModel,
  type PropertiesConfigInputColorType,
  type PropertiesConfigInputConnectionListFactoryMetadata,
  type PropertiesConfigInputContainerItemActionModel,
  type PropertiesConfigInputContainerItemUnifiedActionModel,
  type PropertiesConfigInputListColumnModel,
  type PropertiesConfigInputListCustomFieldModel,
  type PropertiesConfigInputListFiltersModel,
  type PropertiesConfigInputOptionsActionConfig,
  type PropertiesConfigInputOptionsAlign,
  type PropertiesConfigInputOptionsChecks,
  type PropertiesConfigInputOptionsCollectionList,
  type PropertiesConfigInputOptionsColor,
  type PropertiesConfigInputOptionsContainerAction,
  type PropertiesConfigInputOptionsContainerVisibility,
  type PropertiesConfigInputOptionsCoords,
  type PropertiesConfigInputOptionsIconField,
  type PropertiesConfigInputOptionsRadios,
  type PropertiesConfigInputOptionsRadius,
  type PropertiesConfigInputOptionsRatio,
  type PropertiesConfigInputOptionsRemoteText,
  type PropertiesConfigInputOptionsSelect,
  type PropertiesConfigInputOptionsSelectApiSelectors,
  type PropertiesConfigInputOptionsSelectDataSource,
  type PropertiesConfigInputOptionsSelectDataValue,
  type PropertiesConfigInputOptionsSeparator,
  type PropertiesConfigInputOptionsSlider,
  type PropertiesConfigInputOptionsSliderUnitConfig,
  type PropertiesConfigInputOptionsSpacingAndAlignment,
  type PropertiesConfigInputOptionsSubConfig,
  type PropertiesConfigInputOptionsTagsColorPalette,
  type PropertiesConfigInputOptionsText,
  type PropertiesConfigInputOptionsTextArea,
  type PropertiesConfigInputOptionsTextInfo,
  type PropertiesConfigInputOptionsTimeRange,
  type PropertiesConfigInputOptionsToggle,
  type PropertiesConfigInputOptionsUpload,
  type PropertiesConfigInputOptionsWysiwygArea,
  type PropertiesConfigInputVisibilityModel,
  type PropertiesConfigItemDefinition,
  type PropertiesConfigSection,
  type PropertiesConfigSubsection,
  type PropertiesConfigSubsectionTrigger,
  type PropertiesEditorDataSourceConfig,
  type PropertiesEditorItemValueChanged,
  type PropertyInputType,
  type PropsDisplayValueFn,
  type ResizeBehavior,
  type SchemaColumnSelectOption,
  SearchAndFiltersMode,
  type SelectImageListKind,
  STYLE_CONFIG_PROPERTIES_PATH,
  type SubsectionHideWhenFunction,
  type TableColumnFormat,
  TEXT_FIELD_FORMATS,
  TEXT_INPUT_FORMATS,
  type TitleStyleSubsectionPaths,
  type UnifiedActionsGroup,
  type ViewportMode,
  VISUAL_EFFECT_OPTIONS,
  type VisualEffectType,
} from '@blaze-shared/elements-domain';
import { DEFAULT_OPERATOR_NAMES } from '@blaze-shared/intellisense-feature';
import { SchemaService } from '@blaze-shared/schema-data-access';
import {
  COLUMN_ID_OR_NAME,
  type ColumnQueryLike,
  type ColumnType,
  type DatasetColumn,
  type DatasetColumnFormat,
  findColumn,
  isDatasetColumnFormat,
  type RuntimeEventType,
} from '@blaze-shared/schema-domain';
import {
  countedShareReplay,
  DATE_FORMAT_OPTIONS,
  DATETIME_FORMAT_OPTIONS,
  optionalDestructure,
  type SelectOption,
  type SelectOptionWithIcon,
  toInt,
  toTitleCase,
  type ValueWithUnitOfMeasure,
} from '@blaze-shared/shared-util';
import { isNullish } from '@blaze-shared/shared-util';
import { ThemesService } from '@blaze-shared/theming-data-access';
import { type FontStyleOption, NORMAL_FONT_WEIGHT } from '@blaze-shared/theming-domain';
import {
  findFirstNodeInWorkflowOfType,
  findWorkflowNode,
  getValueByPath,
  isAggregateNode,
  isComputeNode,
  isGetNode,
  type WorkflowNode,
} from '@blaze-shared/workflow-domain';

import { getElementById } from '../+state/elements/elements.selectors';
import { RuntimeFacade } from '../+state/runtime/runtime.facade';
import {
  arePropsWithData,
  arePropsWithDirty,
  arePropsWithWrapperRefs,
  hasProps,
} from '../canvas-elements/element-model/element-props-guards';
import { isVariantOfFormContainer } from '../canvas-elements/element-model/element-type-guards';
import { getFormInputNameByType } from '../canvas-elements/element-model/io';
import { type ChartPropsModel } from '../elements/chart/chart.models';
import {
  IMAGE_EMPTY_STATE_OPTIONS,
  type ImageEmptyThumbnail,
} from '../elements/image/image.models';
import { CollectionListBusService } from '../services/collection-list-bus.service';
import { ElementDefinitionsService } from '../services/element-definitions.service';

import { inputVisibilityModelToExpression } from './elements/expressions.utils';
import {
  validateFontSizeWithLineHeight,
  validateLineHeightWithFontSize,
} from './elements/validation.utils';

/**
 * Fetches the most up to date column from the first dataset (main GET node) in a workflow
 * based on the data props and input key.
 *
 * **IMPORTANT:**
 * - Use when you're sure that the schema is ready
 * - Use in injection context only!
 * @param data element's data properties
 * @param key the key of the input
 * @param columnQueryLike the id or name or an id+name query object used to look for the column
 */
const getFirstDatasetColumnFromDataProps = (
  data: CanvasElementDataProperties[] | undefined,
  key: CanvasElementInputBinding,
  columnQueryLike: ColumnQueryLike,
): DatasetColumn | undefined => {
  const dataSource = getActiveBinding(data, key)?.dataSource;

  let datasetId: string | undefined;
  if (dataSource?.sourceType === 'dataset') {
    datasetId = dataSource.id;
  } else if (dataSource?.sourceType === 'context' || dataSource?.sourceType === 'data-filter') {
    // The real schema must be ready at this point
    const workflowSchema = inject(SchemaService).getWorkflowSchema(dataSource.id);
    datasetId = workflowSchema?.tableId;
  } else {
    console.error('Unsupported source type', dataSource);
    return;
  }

  if (!datasetId) {
    return;
  }

  const columns = inject(SchemaService).getSchema(datasetId)?.columns;
  return findColumn(columns, columnQueryLike);
};

/** Start-point of a props-config for element */
const initConfig = (
  componentTypeName: string,
  sections: PropertiesConfigSection[],
): PropertiesConfig => ({
  componentTypeName,
  sections,
});

/** Creates a top-level section config obj.
 * @param resetable enable restoring default values to a section
 */
const section = (
  path: string,
  label: string,
  subsections: PropertiesConfigSubsection[],
  resetable = false,
  disableWhen?: DisableWhenFn,
): PropertiesConfigSection => ({
  path,
  label,
  resetable,
  subsections,
  disableWhen,
});

const disableElementWhenNoLabelOrIcon =
  (path: string) =>
  (model: any): string | undefined => {
    const action = getValueByPath(model, path) as PropertiesConfigInputContainerItemActionModel;
    return !action?.label && !action?.icon ? 'Disabled until label or icon is provided' : undefined;
  };

const actionConfigSections = (
  actionPath: string,
  options?: {
    buttonPath?: string;
    showIconOnlyToggle?: boolean;
    showIconPositionToggle?: boolean;
    skipVisibility?: boolean;
    actionGenerationType?: ActionKind;
    eventType?: RuntimeEventType;
    hasWorkflow?: (action: PropertiesConfigInputContainerItemUnifiedActionModel<any>) => boolean;
    workflowIdPath?: (
      action: PropertiesConfigInputContainerItemUnifiedActionModel<any>,
    ) => string | undefined;
  },
): PropertiesConfigSection[] => {
  const propsPath = actionPath.substring(0, actionPath.lastIndexOf('.'));
  const disableWhenFn = disableElementWhenNoLabelOrIcon(propsPath);

  const buttonFields: PropertiesConfigItemDefinition[] = [];
  let styleField: PropertiesConfigItemDefinition | undefined;
  // For action list, build button config fields
  const buttonPath = options?.buttonPath;
  if (buttonPath) {
    buttonFields.push(textInput(`${buttonPath}.label`, 'Label'), iconField(`${buttonPath}.icon`));
    if (options?.showIconOnlyToggle) {
      buttonFields.push(
        conditionalField(
          toggleField(`${buttonPath}.showInlineLabel`, 'Show icon only', {
            isNegated: true,
            offsetToggle: true,
          }),
          `${buttonPath}.icon`,
          (value) => !value,
        ),
      );
    }
    if (options?.showIconPositionToggle) {
      buttonFields.push(
        conditionalField(
          iconPositionField(`${buttonPath}.iconPosition`, `${buttonPath}.icon`),
          `${buttonPath}`,
          (value) => !value.icon || value.showInlineLabel === false,
        ),
      );
    }
    if (!options.skipVisibility) {
      buttonFields.push(visibilityField('', buttonPath, 'trigger-action'));
    }

    const fallbackKeyName: keyof PropertiesConfigInputContainerItemActionModel = 'styleSheetId';
    const fallbackPath = `${buttonPath}.${fallbackKeyName}`;
    styleField = observedField(
      selectFieldFromStyleSheets(`${buttonPath}.styleSheetTarget`, 'Style (optional)', {
        styleSheetsElementType: 'button',
        styleSheetsFallbackPath: fallbackPath,
      }),
      (model) => {
        /**
         * Cleanup leftover values from the previous action styles model.
         */
        const fallbackValue = getValueByPath(model, fallbackPath);

        if (!fallbackValue) {
          return {};
        }

        return { [`${buttonPath}.${fallbackKeyName}`]: undefined };
      },
    );
  }

  // Finalize - build section
  const actionSection = section(
    '',
    '',
    [
      subsection(
        propsPath,
        '',
        [
          aiTriggerField(actionPath, {
            actionGenerationType: options?.actionGenerationType,
            hideWhen: (model: any): boolean => {
              const action = getValueByPath<
                PropertiesConfigInputContainerItemUnifiedActionModel<undefined | 'custom'>
              >(model, buttonPath);
              return action?.type !== undefined && action.type !== 'custom';
            },
          }),
          ...buttonFields,
          actionConfig(actionPath, {
            basePath: buttonPath,
            actionGenerationType: options?.actionGenerationType,
            eventType: options?.eventType,
            hideWhen: (model: any): boolean => {
              const action = getValueByPath<
                PropertiesConfigInputContainerItemUnifiedActionModel<undefined | 'custom'>
              >(model, buttonPath);
              return options?.hasWorkflow
                ? !action || !options.hasWorkflow(action)
                : action?.type !== undefined && action.type !== 'custom';
            },
            workflowIdPath: options?.workflowIdPath,
          }),
          styleField,
        ],
        { hideLabel: true, dialogWidth: '500px', labelWidth: '35%' },
      ),
    ],
    false,
    disableWhenFn,
  );

  return [actionSection];
};

//#region Subsections

export type SubsectionOptions = Omit<PropertiesConfigSubsection, 'path' | 'label' | 'items'>;

const subsection = (
  path: string,
  label: string,
  items: (PropertiesConfigItemDefinition | undefined)[],
  options?: SubsectionOptions,
): PropertiesConfigSubsection => ({
  path,
  label,
  items: items.filter((e): e is PropertiesConfigItemDefinition<any> => !!e),
  ...options,
  hideLabel: options?.hideLabel ?? false,
  optional: options?.optional ?? false,
  reverse: options?.reverse ?? false,
});

const titleContentSubsection = (
  pathBase: string,
  additionalFields: PropertiesConfigItemDefinition[] = [],
): PropertiesConfigSubsection =>
  subsection(
    `${pathBase}.title`,
    'Title',
    [
      textInput(`${pathBase}.title.label`, 'Label', {
        displayValue: elementMetaDisplayValue(`${pathBase}.title.label`, 'title'),
      }),
      iconField(`${pathBase}.title.icon`),
      ...additionalFields,
    ],
    { optional: true },
  );

const labelContentSubsection = (
  pathBase: string,
  additionalFields: PropertiesConfigItemDefinition[] = [],
): PropertiesConfigSubsection =>
  subsection(
    `${pathBase}.title`,
    'Label',
    [
      textInput(`${pathBase}.title.label`, 'Label', {
        displayValue: elementMetaDisplayValue(`${pathBase}.title.label`, 'title'),
      }),
      iconField(`${pathBase}.title.icon`),
      conditionalField(
        textInput(`${pathBase}.title.tooltip`, 'Tooltip'),
        `${pathBase}.title.enabled`,
        (_, __, params) => !params.isOnForm,
      ),
      ...additionalFields,
    ],
    { optional: true },
  );

const titleStyleSubsection = (paths: TitleStyleSubsectionPaths): PropertiesConfigSubsection =>
  subsection(
    'style.titleBlock',
    'Title',
    [
      colorField(paths.color, 'Color', {
        allowGradient: false,
        allowNone: false,
      }),
      ...(paths.descriptionColor
        ? [
            colorField(paths.descriptionColor, 'Description', {
              allowGradient: false,
              allowNone: false,
            }),
          ]
        : []),
      ...fontFamilyAndWeightFields(paths.fontFamily, paths.fontWeight),
      fontStyleField(paths.fontStyle, 'Style', ['bold']),
      sliderField(paths.fontSize, 'Size', [{ min: 8, max: 72, unit: 'px' }]),
      horizontalAlignField(paths.horizontalAlign, {
        defaultValue: 'left',
        label: 'Position',
      }),
      ...conditionalFields(
        [
          separator('Icon'),
          colorField(paths.iconColor, 'Color', {
            allowGradient: false,
            allowNone: false,
          }),
          horizontalPositionField(paths.iconPosition, 'Position', 'left', paths.icon),
          sliderField(paths.iconSize, 'Size', [{ min: 8, max: 72, unit: 'px' }]),
        ],
        paths.icon,
        (value) => isNullish(value),
      ),
    ],
    {
      hideWhen: (_, __, props) => !(getValueByPath(props, paths.enabled) ?? true),
    },
  );

const labelStyleSubsection = (
  paths: LabelStyleSubsectionPaths,
  options?: LabelStyleSubsectionOptions,
): PropertiesConfigSubsection => {
  const positionOptions: IconSelectOption<LabelPosition>[] = [
    { value: 'left', label: 'Left', icon: 'icon_direction_left_24' },
    { value: 'right', label: 'Right', icon: 'icon_direction_right_24' },
    { value: 'top', label: 'Top', icon: 'icon_direction_up_24' },
  ];

  const lineHeightPath = paths.lineHeight ?? '';

  const alignKnob =
    options?.overrides?.horizontalAlign ??
    horizontalAlignField(paths.horizontalAlign, {
      noCenter: true,
      defaultValue: 'left',
    });

  const positionKnob = paths.position
    ? [
        positionField(
          paths.position,
          'Position',
          positionOptions.filter((position) =>
            options?.excludedPositions?.length
              ? !options.excludedPositions.includes(position.value)
              : true,
          ),
          { defaultValue: 'left' },
        ),
      ]
    : [];

  return subsection(
    'content.title',
    'Label',
    [
      colorField(paths.color, 'Color', {
        allowGradient: false,
        allowNone: false,
      }),
      ...(paths.descriptionColor
        ? [
            colorField(paths.descriptionColor, 'Description', {
              allowGradient: false,
              allowNone: false,
            }),
          ]
        : []),
      ...fontFamilyAndWeightFields(paths.fontFamily, paths.fontWeight),
      fontStyleField(paths.fontStyle, 'Style', ['bold']),
      observedField(
        sliderField(paths.fontSize, 'Size', [{ min: 8, max: 72, unit: 'px' }]),
        (props, value: ValueWithUnitOfMeasure) => {
          return validateLineHeightWithFontSize(props, value, lineHeightPath);
        },
      ),
      observedField(
        sliderField(lineHeightPath, 'Line height', [{ min: 8, max: 72, unit: 'px' }]),
        (props, value: ValueWithUnitOfMeasure) => {
          return validateFontSizeWithLineHeight(props, value, paths.fontSize);
        },
      ),
      ...positionKnob,
      ...(paths.width && paths.position
        ? [
            conditionalField(
              sliderField(paths.width, 'Width', {
                units: [{ min: 0, max: 80, unit: '%', step: 1 }],
                customValue: DETAILS_VIEW_CUSTOM_VALUE,
                displayValue: elementMetaDisplayValue(paths.width, 'titleWidth'),
              }),
              paths.position,
              (model: unknown) => model === 'top',
            ),
          ]
        : []),
      alignKnob,
      ...conditionalFields(
        [
          separator('Icon'),
          colorField(paths.iconColor ?? 'content.title.iconColor', 'Color', {
            allowGradient: false,
            allowNone: false,
          }),
          horizontalPositionField(paths.iconPosition, 'Position', 'left', paths.icon),
          sliderField(paths.iconSize, 'Size', [{ min: 8, max: 72, unit: 'px' }]),
        ],
        paths.icon,
        (value) => isNullish(value),
      ),
    ],
    {
      hideWhen: (_, __, props) => !(getValueByPath(props, paths.enabled) ?? true),
    },
  );
};

const elementSizeSubsection = (
  minSize: number,
  maxWidth: number,
  maxHeight: number,
  widthSizingOptions: ElementSizingMode[],
  heightSizingOptions: ElementSizingMode[],
): PropertiesConfigSubsection => {
  return subsection(
    'content.misc',
    'Size',
    [
      elementWidthField({ min: minSize, max: maxWidth, unit: 'px', step: 1 }, widthSizingOptions),
      elementHeightField(
        { min: minSize, max: maxHeight, unit: 'px', step: 1 },
        heightSizingOptions,
      ),
    ],
    { hideLabel: true },
  );
};

const columnSizeSubsection = (path: string): PropertiesConfigSubsection =>
  subsection('column.sizing', 'Column Width', [
    disabledField(columnSizeField(path, ''), (model) => {
      const slotIds = getValueByPath<SlotIdOf<'container'>[]>(model, 'root.slots.ids');
      return (slotIds?.length ?? 0) < 2 ? 'Container is not split into columns' : undefined;
    }),
  ]);

const elementFontSubsection = (
  path: string,
  label = 'Font',
  options?: {
    fixedFontSizeOptional?: boolean;
    unitsOrOptions?: PropertiesConfigInputOptionsSliderUnitConfig[];
  },
): PropertiesConfigSubsection =>
  subsection(path, label, [
    ...fontFamilyAndWeightFields(`${path}.fontFamily`, `${path}.fontWeight`),
    fontStyleField(`${path}.style`, 'Style', ['bold', 'line-through']),
    ...(options?.fixedFontSizeOptional
      ? [
          separator(),
          toggleField(`${path}.fontSizeEnabled`, 'Fixed font size'),
          conditionalField(
            sliderField(
              `${path}.fontSize`,
              'Size',
              options?.unitsOrOptions ?? [{ min: 8, max: 72, unit: 'px' }],
            ),
            `${path}.fontSizeEnabled`,
            (value) => !value,
          ),
        ]
      : [
          sliderField(
            `${path}.fontSize`,
            'Size',
            options?.unitsOrOptions ?? [{ min: 8, max: 72, unit: 'px' }],
          ),
        ]),
  ]);

const elementBackgroundSubsection = (
  path: string,
  label: string,
  {
    allowGradientFill = true,
    allowImageFill = false,
    allowNoneFill = true,
    allowNoneOutline = true,
    allowPerCorner = true,
    fillLabel = 'Fill',
    hasFill = true,
    hasOutline = true,
    hasOutlineSides = false,
    hasRadius = true,
    hasWidth = true,
    hideEffectPanel = false,
    hoverPath = '',
    selectedRowPath = '',
    hideItemHover = undefined as ConditionalFieldHideWhenFn | undefined,
    hideItemSelected = undefined as ConditionalFieldHideWhenFn | undefined,
    extraConfigItems = [] as PropertiesConfigItemDefinition[],
  } = {},
): PropertiesConfigSubsection =>
  subsection(path, label, [
    ...extraConfigItems,
    ...(hasFill
      ? [
          colorField(`${path}.fill`, fillLabel, {
            allowGradient: allowGradientFill,
            allowImage: allowImageFill,
            allowNone: allowNoneFill,
          }),
        ]
      : []),
    ...(allowImageFill
      ? [
          conditionalField(
            imageBehaviorField(`${path}.imageBehavior`, undefined, { hideLabel: true }),
            `${path}.fill.selectorType`,
            (value: PropertiesConfigInputColorType) => value !== 'image',
          ),
        ]
      : []),
    ...resolveConditionalColorField(hoverPath, 'Item hover', hideItemHover),
    ...resolveConditionalColorField(selectedRowPath, 'Item selected', hideItemSelected),
    ...(hasOutline
      ? [
          colorField(`${path}.outline`, 'Outline', {
            allowGradient: false,
            hasOutlineSides,
            allowNone: allowNoneOutline,
          }),
        ]
      : []),
    ...(hasWidth
      ? [sliderField(`${path}.outlineWidth`, 'Width', [{ min: 0, max: 10, unit: 'px' }])]
      : []),
    ...(hasRadius ? [radiusField(`${path}.radius`, undefined, { allowPerCorner })] : []),
    ...(hideEffectPanel ? [] : visualEffectField(`${path}.visualEffect`)),
  ]);

function resolveConditionalColorField(
  path: string,
  label: string,
  hideWhen?: ConditionalFieldHideWhenFn,
): PropertiesConfigItemDefinition[] {
  if (!path) {
    return [];
  }
  if (hideWhen) {
    return [conditionalField(colorField(path, label, {}), '', hideWhen)];
  }
  return [colorField(path, label, {})];
}

const elementUIPartStyleSubsection = (
  path: string,
  label: string,
  subsectionOptions?: SubsectionOptions,
): PropertiesConfigSubsection =>
  subsection(
    path,
    label,
    [
      colorField(`${path}.text`, 'Text', { allowGradient: false, allowNone: false }),
      colorField(`${path}.fill`, 'Fill', {}),
      colorField(`${path}.outline`, 'Outline', { allowGradient: false }),
      sliderField(`${path}.outlineWidth`, 'Width', [
        {
          min: 0,
          max: 10,
          unit: 'px',
        },
      ]),
      radiusField(`${path}.radius`, undefined, { allowPerCorner: false }),
    ],
    subsectionOptions,
  );

const calendarDaysStyleSubsection = (
  basePath: string,
  label = 'Date Selector',
  additionalFields: PropertiesConfigItemDefinition[] = [],
  subsectionOptions?: SubsectionOptions,
): PropertiesConfigSubsection => {
  const stateOptions: SelectOption<CalendarDateSelectorState>[] = [
    { value: 'default', label: 'Default' },
    { value: 'hover', label: 'Hover' },
    { value: 'selected', label: 'Selected' },
  ];

  function dateSelectorStyleFields(path: string): PropertiesConfigItemDefinition[] {
    return [
      colorField(`${path}.day`, 'Day', { allowGradient: false, allowNone: false }),
      colorField(`${path}.fill`, 'Fill', { allowGradient: false, allowNone: false }),
      colorField(`${path}.outline`, 'Outline', { allowGradient: false }),
      sliderField(`${path}.outlineWidth`, 'Width', [
        {
          min: 0,
          max: 10,
          unit: 'px',
        },
      ]),
      radiusField(`${path}.radius`, 'Radius', { allowPerCorner: false }),
    ];
  }

  return subsection(
    basePath,
    label,
    [
      radiosField(`${basePath}.currentState`, 'State', stateOptions, { hideLabel: true }),
      ...conditionalFields(
        dateSelectorStyleFields(`${basePath}.default`),
        `${basePath}.currentState`,
        (state: CalendarDateSelectorState) => state !== 'default',
      ),
      ...conditionalFields(
        dateSelectorStyleFields(`${basePath}.hover`),
        `${basePath}.currentState`,
        (state: CalendarDateSelectorState) => state !== 'hover',
      ),
      ...conditionalFields(
        dateSelectorStyleFields(`${basePath}.selected`),
        `${basePath}.currentState`,
        (state: CalendarDateSelectorState) => state !== 'selected',
      ),
      separator(),
      colorField(`${basePath}.dayWithEvents`, 'Day with events', {
        allowGradient: false,
        allowNone: false,
      }),
      ...additionalFields,
    ],
    subsectionOptions,
  );
};

const dataSubsection = (
  bindingPath: string,
  label: string,
  unboundFields: PropertiesConfigItemDefinition[],
  dataFields: PropertiesConfigItemDefinition[],
  {
    enabledByDefault = false,
    subsectionOptions = {},
    prefixFields = [],
    suffixFields = [],
    readonlyWhen = undefined,
  }: {
    enabledByDefault?: boolean;
    subsectionOptions?: SubsectionOptions;
    prefixFields?: PropertiesConfigItemDefinition[];
    suffixFields?: PropertiesConfigItemDefinition[];
    readonlyWhen?: (model: any) => boolean;
  } = {},
): PropertiesConfigSubsection =>
  subsection(
    bindingPath,
    label,
    [
      ...prefixFields,
      ...conditionalFields(
        dataFields,
        bindingPath,
        (value: unknown) => !enabledByDefault && !value,
      ),
      ...conditionalFields(
        unboundFields,
        bindingPath,
        (value: unknown) => enabledByDefault || !!value,
      ),
      ...suffixFields,
    ],
    {
      ...subsectionOptions,
      toggle: { label: 'Dynamic', path: bindingPath, readonly: enabledByDefault, readonlyWhen },
    },
  );

type ModPrefix =
  | typeof READ_SLOT_PARENT_PATH_PREFIX
  | typeof READ_EXCLUSIVE_SLOT_PARENT_PATH_PREFIX
  | '';

interface VisibilitySubsectionOptions extends SubsectionOptions {
  visibilityFieldPath?: string;
}

const visibilitySubsection = (
  modPrefix: ModPrefix = '',
  subsectionOptions?: VisibilitySubsectionOptions,
): PropertiesConfigSubsection =>
  subsection(
    'content',
    'Visible',
    [
      viewportVisibilityField(`${modPrefix}content.visible.viewports`, 'Show on'),
      separator('', { labelOnLine: 'and' }),
      visibilityField(modPrefix, subsectionOptions?.visibilityFieldPath, undefined, {
        viewportsEnabled: true,
      }),
    ],
    {
      hideLabel: true,
      ...subsectionOptions,
    },
  );

const visibilityColumnSubsection = (
  modPrefix: ModPrefix = '',
  subsectionOptions?: VisibilitySubsectionOptions,
): PropertiesConfigSubsection =>
  subsection(
    'content',
    'Column visibility',
    [
      visibilityField(
        modPrefix,
        subsectionOptions?.visibilityFieldPath ?? 'content.columnsVisibility',
        undefined,
        {
          viewportsEnabled: true,
          columnPickerEnabled: true,
        },
      ),
    ],
    {
      hideLabel: false,
      ...subsectionOptions,
    },
  );

const updateVisibilityDataSourceOnValueChange =
  (modPrefix: ModPrefix, path?: string): PropertiesEditorItemValueChanged =>
  (model, value: PropertiesConfigInputVisibilityModel, syncModel, elementId, slotId) => {
    const expression = inputVisibilityModelToExpression(value);

    let forceSetToElement = false;
    if (slotId && modPrefix === READ_EXCLUSIVE_SLOT_PARENT_PATH_PREFIX) {
      const slotsCount = getValueByPath<unknown[]>(model, 'root.slots.ids')?.length ?? 0;
      forceSetToElement = slotsCount <= 1;
    }
    const useElementPath = !!path && !forceSetToElement;
    if (expression.length) {
      if (useElementPath) {
        return {
          [`${modPrefix}${path}.visibilityExpression`]: expression,
        };
      }

      return {
        [`${modPrefix}data.?key:visible.dataSource.sourceType`]: 'expression',
        [`${modPrefix}data.?key:visible.dataSource.expression`]: expression,
        [`${modPrefix}data.?key:visible.enabled`]: true,
      };
    }

    if (useElementPath) {
      return {
        [`${modPrefix}${path}.visibilityExpression`]: expression,
      };
    }

    return {
      [`${modPrefix}data.?key:visible.dataSource.expression`]: [],
      [`${modPrefix}data.?key:visible.enabled`]: false,
    };
  };

const visibilityField = (
  modPrefix: ModPrefix = '',
  path?: string,
  displayMode?: 'trigger-button' | 'trigger-action' | 'trigger-menu',
  options?: {
    viewportsEnabled?: boolean;
    columnPickerEnabled?: boolean;
  },
): PropertiesConfigItemDefinition =>
  observedField(
    buttonVisibleField(`${modPrefix}${path || 'content'}.visible`, {
      displayMode,
      viewportsEnabled: options?.viewportsEnabled,
      columnPickerEnabled: options?.columnPickerEnabled,
    }),
    updateVisibilityDataSourceOnValueChange(modPrefix, path),
  );

const viewportVisibilityField = (
  path: string,
  label: string,
  inputOptions?: Partial<PropertiesConfigInputOptionsChecks<'icon'>>,
): PropertiesConfigItemDefinition<'checks'> => {
  const options: IconSelectOption<ViewportMode>[] = [
    { value: 'desktop', label: 'Desktop', icon: 'icon_screen_size_desktop_16' },
    { value: 'tablet', label: 'Tablet', icon: 'icon_screen_size_tablet_16' },
    { value: 'mobile', label: 'Mobile', icon: 'icon_screen_size_phone_16' },
  ];

  const beforeSave = (value: ViewportMode[] | undefined): ViewportMode[] | undefined =>
    value?.length === 0 || (value?.length && options.every((o) => value.includes(o.value)))
      ? undefined
      : value;

  const beforeRead = (value: ViewportMode[] | undefined): ViewportMode[] | undefined =>
    value?.length ? value : ['desktop', 'tablet', 'mobile'];

  return {
    path,
    label,
    inputType: 'checks',
    inputOptions: {
      options,
      renderType: 'icon',
      renderMode: 'stretched',
      beforeSave,
      beforeRead,
      ...inputOptions,
    },
  };
};

const complexSearchAndFiltersSubsection = ({
  label = 'Filters',
  hideWhen = undefined,
}: {
  label?: string;
  hideWhen?: SubsectionHideWhenFunction;
}): PropertiesConfigSubsection =>
  subsection(
    'content.searchAndFilters',
    label,
    [
      ...disabledFields(
        [
          radiosField(
            'content.searchAndFilters.mode',
            '',
            [
              { value: SearchAndFiltersMode.PREDEFINED, label: 'Predefined' },
              { value: SearchAndFiltersMode.FILTER_BUILDER, label: 'Filter Builder' },
            ],
            { hideLabel: true, leftIndent: true },
          ),
          ...conditionalFields(
            [
              listFiltersField('content.searchAndFilters.filters', 'Predefined Filters', {
                hideLabel: true,
                verticalPadding: false,
              }),
            ],
            'content.searchAndFilters.mode',
            (value) => value !== SearchAndFiltersMode.PREDEFINED,
          ),
          ...conditionalFields(
            [containerFilterBuilderColumnsField('content.filterBuilderConfig')],
            'content.searchAndFilters.mode',
            (value) => value !== SearchAndFiltersMode.FILTER_BUILDER,
          ),
          separator(),
          containerSearchFilterField('content.searchAndFilters', 'Search'),
        ],
        (model) => {
          const dataSource: DataBindingSourceInfo | null | undefined = getValueByPath(
            model,
            'data.?key:value.dataSource',
          );
          if (!dataSource) {
            return 'The Value of the element is not bound';
          }
          const runtimeFacade = inject(RuntimeFacade);
          // Supported
          if (dataSource.sourceType === 'context' || dataSource.sourceType === 'data-filter') {
            const workflow = runtimeFacade.getWorkflowById(dataSource.id);
            if (workflow && findFirstNodeInWorkflowOfType(workflow, isGetNode)) {
              return;
            }
          }
          return 'Selected data source does not support search and filters configuration';
        },
      ),
    ],
    {
      hideWhen,
      itemAdd: (model: { root: { elementId: CanvasElementId } }): undefined => {
        inject(CollectionListBusService).postItemAdd({
          elementId: model.root.elementId,
          path: 'content.searchAndFilters.filters',
        });
      },
      itemAddVisible: (model: PropsOf<'table'>): boolean =>
        model.content.searchAndFilters?.mode === 'PREDEFINED',
    },
  );

const searchAndFiltersSubsection = (): PropertiesConfigSubsection =>
  subsection(
    'content.searchAndFilters',
    'Search and Filters',
    [
      ...disabledFields(
        [
          listFiltersField('content.searchAndFilters.filters', 'Search and Filters'),
          toggleField('content.searchAndFilters.searchEnabled', 'Show search bar'),
        ],
        (model) =>
          !getValueByPath(model, 'data.?key:value.dataSource')
            ? 'The Value of the element is not bound'
            : undefined,
      ),
    ],
    { hideLabel: true },
  );

const imagePropertiesFields = (
  path: string,
  defaultValue: Partial<PropertiesConfigImagePropertiesModel> | undefined,
  shapeOptions: SelectOption<ImageShape>[] = IMAGE_SHAPE_OPTIONS,
  onShapeChange?: PropertiesEditorItemValueChanged,
  prefixBehaviorLabel?: boolean,
): PropertiesConfigItemDefinition[] => [
  selectFieldFromImageList(
    'image-empty-state-big',
    `${path}.emptyThumbnail`,
    'Empty state',
    [{ label: 'None', value: '' }, ...IMAGE_EMPTY_STATE_OPTIONS],
    { allowNone: false, showSelection: true, defaultValue: '' },
  ),
  conditionalField(
    textInput(`${path}.emptyText`, ''),
    `${path}.emptyThumbnail`,
    (emptyThumbnail: ImageEmptyThumbnail) => emptyThumbnail !== 'custom-text',
  ),
  conditionalField(
    selectFieldFromDataBinding(`${path}.initialsColumn`, '', 'value', {
      dataBindingsMode: 'first-dataset',
      valueField: COLUMN_ID_OR_NAME,
      allowNone: false,
      optionsFilter: columnDataTypeFilter('String'),
    }),

    `${path}.emptyThumbnail`,
    (emptyThumbnail: ImageEmptyThumbnail) => emptyThumbnail !== 'initials',
  ),
  observedField(
    selectFieldFromList(`${path}.shape`, 'Shape', shapeOptions, {
      showSelection: true,
      showNoneOption: false,
      defaultValue: defaultValue?.shape,
    }),
    onShapeChange || ((): ReturnType<PropertiesEditorItemValueChanged> => ({})),
  ),
  radiosField(
    `${path}.behavior`,
    prefixBehaviorLabel ? 'Image behavior' : 'Behavior',
    [
      { value: 'cover', label: 'Cover' },
      { value: 'contain', label: 'Fit' },
    ],
    { defaultValue: 'cover' },
  ),
  conditionalField(
    radiusField(`${path}.radius`, 'Radius', {
      defaultValue: defaultValue?.radius,
    }),
    `${path}.shape`,
    (shape?: ImageShape) => shape === 'circle',
  ),
  colorField(`${path}.outline`, 'Outline', {
    allowGradient: false,
    defaultValue: defaultValue?.outline,
  }),
  conditionalField(
    sliderField(`${path}.outlineWidth`, 'Width', {
      units: [{ min: 0, max: 10, unit: 'px' }],
      defaultValue: defaultValue?.outlineWidth,
    }),
    `${path}.outline`,
    (outline?: PropertiesConfigInputColorModel) => outline?.selectorType === 'none',
  ),
];

const styleField = (
  inputOptions?: GenericInputOptions,
): PropertiesConfigItemDefinition<'style'> => {
  return {
    path: STYLE_CONFIG_PROPERTIES_PATH,
    label: 'Style',
    inputType: 'style',
    inputOptions,
  };
};

const styleSheetSubsection = (inputOptions?: GenericInputOptions): PropertiesConfigSubsection =>
  subsection(STYLE_CONFIG_PROPERTIES_PATH, 'Style', [styleField(inputOptions)], {
    hideLabel: true,
  });

const triggerSubsection = (
  trigger: PropertiesConfigSubsectionTrigger,
): PropertiesConfigSubsection =>
  subsection('', '', [], {
    hideLabel: true,
    hideWhen: () => true,
    trigger,
  });

const labelSubsection = (label: string): PropertiesConfigSubsection =>
  subsection('', label, [], {
    labelOnly: true,
  });

//#endregion Subsections

//#region Utils

type ConditionalFieldHideWhenFn = (
  targetPathValue: any,
  model: any,
  params: ConditionalFieldParams,
) => boolean;
/**
 * makes the field hidden when {@param hideWhen} returns true,
 * based on another model field value ({@param targetPath})
 * @param field field config object of which `inputOptions` are extended
 * @param targetPath path to model value which determines {@param field} visibility
 * @param hideWhen predicate function accepts model value (via {@param targetPath}) and returns boolean
 */
const conditionalField = <T extends PropertyInputType>(
  field: PropertiesConfigItemDefinition<T>,
  targetPath: string,
  hideWhen: ConditionalFieldHideWhenFn,
): PropertiesConfigItemDefinition<T> =>
  conditionalFields([field], targetPath, hideWhen)[0] as PropertiesConfigItemDefinition<T>;

const conditionalFields = (
  fields: PropertiesConfigItemDefinition[],
  targetPath: string,
  hideWhen: ConditionalFieldHideWhenFn,
): PropertiesConfigItemDefinition[] =>
  fields.map((field) => ({
    ...field,
    inputOptions: {
      ...field.inputOptions,
      hideWhen: (model: any, params: ConditionalFieldParams): boolean => {
        const nestedCondition = field.inputOptions?.hideWhen
          ? field.inputOptions?.hideWhen(model, params)
          : false;
        return (
          nestedCondition ||
          hideWhen(targetPath ? getValueByPath(model, targetPath) : null, model, params)
        );
      },
    },
  }));

const disabledField = <T extends PropertyInputType>(
  field: PropertiesConfigItemDefinition<T>,
  disableWhen: DisableWhenFn,
): PropertiesConfigItemDefinition<T> => ({
  ...field,
  inputOptions: {
    ...field.inputOptions,
    disableWhen,
  },
});
const disabledFields = (
  field: PropertiesConfigItemDefinition[],
  disableWhen: DisableWhenFn,
): PropertiesConfigItemDefinition[] => {
  return field.map((f) => ({
    ...f,
    inputOptions: {
      ...f.inputOptions,
      disableWhen,
    },
  }));
};
interface SeparatedFieldOptions {
  hideSeparatorWhenHidden?: boolean;
  fullWidthSeparator?: boolean;
  hideTopSeparator?: boolean;
  label?: string;
}

const separatedField = <T extends PropertyInputType>(
  field: PropertiesConfigItemDefinition<T>,
  labelOrOptions: string | SeparatedFieldOptions = '',
): PropertiesConfigItemDefinition<T> => {
  const options =
    typeof labelOrOptions === 'string'
      ? { label: labelOrOptions }
      : { label: '', ...labelOrOptions };
  return {
    ...field,
    startsGroup: options.label,
    hideSeparatorWhenHidden: options.hideSeparatorWhenHidden,
    hideTopSeparator: options.hideTopSeparator,
    fullWidthSeparator: options.fullWidthSeparator,
  };
};

const separator = (
  label?: string,
  inputOptions?: PropertiesConfigInputOptionsSeparator,
): PropertiesConfigItemDefinition<'separator'> => ({
  path: '',
  label: label ?? '',
  inputType: 'separator',
  inputOptions,
});

const lockOverride = <T extends PropertyInputType>(
  field: PropertiesConfigItemDefinition<T>,
): PropertiesConfigItemDefinition<T> => ({ ...field, subgroupLockOverride: true });

const dataSourcedField = <T extends PropertyInputType>(
  field: PropertiesConfigItemDefinition<T>,
  dataBindings: PropertiesEditorDataSourceConfig[],
): PropertiesConfigItemDefinition<T> => ({ ...field, dataBindings });

const observedField = <T extends PropertyInputType>(
  field: PropertiesConfigItemDefinition<T>,
  valueChanged: PropertiesEditorItemValueChanged,
): PropertiesConfigItemDefinition<T> => ({ ...field, valueChanged });

const adornField = <T extends PropertyInputType, D = unknown>(
  field: PropertiesConfigItemDefinition<T>,
  adornData: D,
): PropertiesConfigItemDefinition<T> => ({
  ...field,
  inputOptions: { ...field.inputOptions, ...adornData },
});

type ColumnFilter = (model: any, options: SelectOption<any>[]) => SelectOption<any>[];

/**
 * Filters the select options based on the provided column formats. This function works also for schema.
 * @param formats  An array of column formats to be considered in the filter. Formats may have a '!' prefix for exclusion
 * @returns  A filtering function that takes a model and select options, and returns the filtered options
 */
function columnFormatFilter(
  ...formats: (TableColumnFormat | `!${TableColumnFormat}`)[]
): ColumnFilter {
  return ((
    model: any,
    options: SelectOption<{ format: TableColumnFormat | DatasetColumnFormat }>[],
  ) => {
    const filterIncluded = formats.some((format) => !format.startsWith('!'));
    const filterExcluded = formats.some((format) => format.startsWith('!'));
    return options.filter(({ value: { format } }) => {
      if (isDatasetColumnFormat(format)) {
        const formatType: TableColumnFormat | undefined = format.formatType
          ? DATASET_FORMAT_TO_TABLE_FORMAT[format.formatType]
          : undefined;
        if (!formatType) {
          return true;
        }
        const isIncluded = filterIncluded ? formats.includes(formatType) : true;
        const isExcluded = filterExcluded ? formats.includes(`!${formatType}`) : false;
        return isIncluded && !isExcluded;
      }
      const isIncluded = filterIncluded ? formats.includes(format) : true;
      const isExcluded = filterExcluded ? formats.includes(`!${format}`) : false;
      return isIncluded && !isExcluded;
    });
  }) as (model: any, options: SelectOption[]) => SelectOption[];
}

type ColumnDataTypeFilter = ColumnType | `!${ColumnType}`;
/**
 * Filters the select options based on the provided column dataTypes
 * @param dataTypes  An array of column dataTypes to be considered in the filter. Formats may have a '!' prefix for exclusion
 * @returns  A filtering function that takes a model and select options, and returns the filtered options
 */
function columnDataTypeFilter(
  dataTypes: ColumnDataTypeFilter | ColumnDataTypeFilter[],
  columnTypes?: ColumnDataTypeFilter | ColumnDataTypeFilter[],
): ColumnFilter {
  if (!Array.isArray(dataTypes)) {
    dataTypes = [dataTypes];
  }
  const includeDataTypes = dataTypes.some((dataType) => !dataType.startsWith('!'));
  const excludeDataTypes = dataTypes.some((dataType) => dataType.startsWith('!'));

  if (columnTypes && !Array.isArray(columnTypes)) {
    columnTypes = [columnTypes];
  }
  const includeColumnTypes =
    columnTypes?.some((columnType) => !columnType.startsWith('!')) ?? false;
  const excludeColumnTypes = columnTypes?.some((columnType) => columnType.startsWith('!')) ?? false;

  return ((
    model: any,
    options: (SelectOption & { dataType: ColumnType; columnType: ColumnType })[],
  ) => {
    return options.filter(({ dataType, columnType }) => {
      const isIncludedDataType = includeDataTypes ? dataTypes.includes(dataType) : true;
      const isExcludedDataType = excludeDataTypes ? dataTypes.includes(`!${dataType}`) : false;
      const isIncludedColumnType = includeColumnTypes ? columnTypes?.includes(columnType) : true;
      const isExcludedColumnType = excludeColumnTypes
        ? columnTypes?.includes(`!${columnType}`)
        : false;
      return (
        isIncludedDataType && isIncludedColumnType && !isExcludedDataType && !isExcludedColumnType
      );
    });
  }) as any;
}

/**
 * Filters out columns with multipleValues
 * @returns A filtering fnction
 */
function columnMultipleFilter(): ColumnFilter {
  return (model: any, options: SelectOption<{ multipleValues: boolean }>[]) => {
    return options.filter(({ value: { multipleValues } }) => !multipleValues);
  };
}

/**
 * Combines multiple filtering functions into a single one
 * @param filters source filtering functions
 * @returns A filtering fnction
 */
function columnFiltersCombination(...filters: ColumnFilter[]): ColumnFilter {
  return (model: any, options: SelectOption<{ multipleValues: boolean }>[]) => {
    return filters.reduce((opts, filter) => filter(model, opts), options);
  };
}

export function mapSizingModesToOptions(
  sizingOptions?: ElementSizingMode[],
): SelectOption<ElementSizingMode>[] | undefined {
  return sizingOptions?.map((o) => ({
    value: o,
    label: toTitleCase(o),
  }));
}

/**
 * An `elementWidthField`/`elementHeightField` `optionsFilter` that relabels one sizing mode's
 * option: `min` to "Fit-Content" for specific elements without a global rename.
 */
export const relabelSizingMinToFitContent = (
  _model: unknown,
  options: SelectOption<ElementSizingMode>[],
): SelectOption<ElementSizingMode>[] =>
  options.map((option) =>
    option.value === 'min' ? { ...option, label: 'Fit-Content', hasLongLabel: true } : option,
  );

//#endregion

//#region fields: checks

const fontStyleField = (
  path: string,
  label: string,
  exclude?: FontStyleOption[],
): PropertiesConfigItemDefinition<'checks'> => {
  const options = FONT_STYLE_OPTIONS.filter((option) => {
    if (!exclude?.length) {
      return true;
    }

    return !exclude.includes(option.value);
  });

  return {
    path,
    inputType: 'checks',
    label,
    inputOptions: {
      renderType: 'icon',
      options,
      defaultValue: [],
    },
  };
};

const checksField = (
  path: string,
  label: string,
  options: IconSelectOption[] | Partial<PropertiesConfigInputOptionsChecks>,
): PropertiesConfigItemDefinition<'checks'> => ({
  path,
  inputType: 'checks',
  label,
  inputOptions: {
    renderType: 'text',
    options: Array.isArray(options) ? options : [],
    defaultValue: [],
    ...(Array.isArray(options) ? undefined : options),
  },
});

//#endregion

//#region fields: color

const colorField = (
  path: string,
  label: string,
  inputOptions?: Partial<PropertiesConfigInputOptionsColor>,
): PropertiesConfigItemDefinition<'color'> => ({
  path,
  label,
  inputType: 'color',
  inputOptions: {
    allowImage: false,
    allowNone: true,
    allowSolid: true,
    allowGradient: true,
    hasOutlineSides: false,
    showTableLinesOptions: false,
    skipChangeToNone: false,
    ...inputOptions,
  },
});

const colorSimpleField = (
  path: string,
  label: string,
): PropertiesConfigItemDefinition<'colorSimple'> => ({
  path,
  label,
  inputType: 'colorSimple',
});

//#endregion

//#region fields: coords

const coordsField = (
  path: string,
  label: string,
  inputOptions?: Partial<PropertiesConfigInputOptionsCoords>,
): PropertiesConfigItemDefinition<'coords'> => ({
  path,
  label,
  inputType: 'coords',
  inputOptions: {
    ...inputOptions,
  },
});

//#endregion

//#region fields: Custom Template

const customTemplateField = (path: string): PropertiesConfigItemDefinition<'customTemplate'> => ({
  path,
  label: 'HTML EDITOR',
  inputType: 'customTemplate',
});

//#endrefion

//#region fields: datetime

//#endregion

//#region fields: icon

/** pre-labeled ('Icon') icon knob */
const iconField = (
  path: string,
  label = 'Icon',
  inputOptions?: PropertiesConfigInputOptionsIconField,
): PropertiesConfigItemDefinition<'icon'> => ({
  path,
  label,
  inputType: 'icon',
  inputOptions,
});

//#endregion

//#region fields: list

const listField = (path: string, label: string): PropertiesConfigItemDefinition<'list'> => ({
  path,
  label,
  inputType: 'list',
});

//#endregion

//#region fields: listLayout

const listLayoutField = (path: string): PropertiesConfigItemDefinition<'listLayout'> => ({
  path,
  inputType: 'listLayout',
  label: '',
  inputOptions: {
    defaultDirection: 'vertical',
    defaultMode: 'hscroll',
    hideDirection: true,
  },
});

//#endregion

//#region fields: navMenuItems

const navMenuItemsField = (
  path: string,
  label: string,
): PropertiesConfigItemDefinition<'navMenuItems'> => ({
  path,
  inputType: 'navMenuItems',
  label,
  inputOptions: {
    itemConfig: (id, type): PropertiesConfigSubsection[] => {
      const pathRoot = `${id}.props`;

      if (type === 'section') {
        return [
          subsection(
            `${pathRoot}`,
            'Section Details',
            [textInput(`${pathRoot}.label`, 'Label'), iconField(`${pathRoot}.icon`)],
            { hideLabel: true, dialogWidth: '300px' },
          ),
        ];
      }

      return [
        subsection(
          `${pathRoot}`,
          'Menu Details',
          [
            textInput(`${pathRoot}.label`, 'Label'),
            iconField(`${pathRoot}.icon`),
            visibilityField('', pathRoot, 'trigger-menu'),
            selectFieldFromList(`${pathRoot}.clickAction`, 'On click', NavMenu.MENU_ACTIONS, {
              showSelection: true,
              allowMultiSelect: false,
            }),
            conditionalField(
              selectFieldFromApi(`${pathRoot}.actionParams`, 'Open page', 'LIST_PAGES', {
                showSelection: true,
              }),
              `${pathRoot}.clickAction`,
              (modelValue) => modelValue !== 'GoToPage',
            ),
            conditionalField(
              textInput(`${pathRoot}.navigateUrl`, 'URL'),
              `${pathRoot}.clickAction`,
              (modelValue) => modelValue !== 'Url',
            ),
          ],
          { hideLabel: true, dialogWidth: '300px' },
        ),
      ];
    },
  },
});

//#endregion

//#region fields: spacing

const spacingAndAlignField = (
  path: string,
  inputOptions: PropertiesConfigInputOptionsSpacingAndAlignment,
): PropertiesConfigItemDefinition<'spacing'> => ({
  path,
  inputType: 'spacing',
  label: '',
  inputOptions,
});

//#endregion

//#region fields: spacingContainer

const spacingAndAlignContainerField = (
  path: string,
  inputOptions: PropertiesConfigInputOptionsSpacingAndAlignment,
): PropertiesConfigItemDefinition<'spacingContainer'> => ({
  path,
  inputType: 'spacingContainer',
  label: '',
  inputOptions,
});

//#endregion

//#region fields: search and filters

const containerSearchFilterField = (
  path: string,
  label = '',
): PropertiesConfigItemDefinition<'containerSearchFilter'> => ({
  path,
  inputType: 'containerSearchFilter',
  label,
});

//#endregion

//#region fields: filter builder columns

const containerFilterBuilderColumnsField = (
  path: string,
  label = '',
): PropertiesConfigItemDefinition<'containerFilterBuilderColumns'> => ({
  path,
  inputType: 'containerFilterBuilderColumns',
  label,
});

//#endregion

//#region fields: radios
const radiosField = (
  path: string,
  label: string,
  options: IconSelectOption[],
  inputOptions?: Partial<PropertiesConfigInputOptionsRadios>,
): PropertiesConfigItemDefinition<'radios'> => ({
  path,
  label,
  inputType: 'radios',
  inputOptions: {
    renderType: 'text',
    stretched: true,
    options,
    ...inputOptions,
  },
});

const positionField = (
  path: string,
  label: string,
  items: IconSelectOption[],
  options?: {
    stretched?: boolean;
    iconPath?: string;
    defaultValue?: string;
  },
  inputOptions?: Partial<PropertiesConfigInputOptionsRadios>,
): PropertiesConfigItemDefinition<'radios'> => ({
  path,
  label,
  inputType: 'radios',
  inputOptions: {
    renderType: 'icon-text',
    stretched: options?.stretched,
    iconPath: options?.iconPath,
    options: items,
    defaultValue: options?.defaultValue,
    ...optionalDestructure(!!options?.iconPath, {
      hideWhen: (model: unknown) => !getValueByPath(model, options?.iconPath),
    }),
    ...inputOptions,
  },
});

const layoutField = (path: string): PropertiesConfigItemDefinition<'radios'> => ({
  path,
  label: 'Layout',
  inputType: 'radios',
  inputOptions: {
    renderType: 'text',
    stretched: true,
    options: [
      { label: 'Row', value: 'row' },
      { label: 'Column', value: 'column' },
    ],
  },
});
const directionField = (
  path: string,
  inputOptions?: Partial<PropertiesConfigInputOptionsRadios>,
): PropertiesConfigItemDefinition<'radios'> => ({
  path,
  label: 'Direction',
  inputType: 'radios',
  inputOptions: {
    renderType: 'text',
    stretched: true,
    options: [
      { label: 'Horizontal', value: 'horizontal' },
      { label: 'Vertical', value: 'vertical' },
    ],
    ...inputOptions,
  },
});

const horizontalPositionField = (
  path: string,
  label = 'Position',
  defaultValue: LabelIconAlignment = 'left',
  iconPath?: string,
): PropertiesConfigItemDefinition<'radios'> =>
  positionField(
    path,
    label,
    [
      { value: 'left', label: 'Left', icon: 'icon_direction_left_24' },
      { value: 'right', label: 'Right', icon: 'icon_direction_right_24' },
    ],
    { iconPath, defaultValue },
  );

const horizontalAlignField = (
  path: string,
  options?: {
    label?: string;
    noCenter?: boolean;
    defaultValue?: 'left' | 'right';
    controlAlign?: 'center';
    icons?: string[];
  },
): PropertiesConfigItemDefinition<'radios'> => {
  const opts = [
    { value: 'left', icon: options?.icons?.[0] ?? 'icon_alignment_left_16' },
    { value: 'right', icon: options?.icons?.[1] ?? 'icon_alignment_right_16' },
  ];

  if (!options?.noCenter) {
    opts.splice(1, 0, { value: 'center', icon: options?.icons?.[2] ?? 'icon_alignment_center_16' });
  }

  return {
    path,
    label: options?.label ?? 'Align',
    inputType: 'radios',
    inputOptions: {
      renderType: 'icon',
      defaultValue: options?.defaultValue ?? 'left',
      options: opts,
      controlAlign: options?.controlAlign,
    },
  };
};

const iconPositionField = (
  path: string,
  iconPath: string,
  defaultValue: LabelIconAlignment = 'left',
): PropertiesConfigItemDefinition<'radios'> =>
  horizontalPositionField(path, 'Icon position', defaultValue, iconPath);

const titlePositionField = (path: string): PropertiesConfigItemDefinition<'radios'> =>
  positionField(
    path,
    'Position',
    [
      { value: 'left', label: 'Left', icon: 'icon_direction_left_24' },
      { value: 'top', label: 'Top', icon: 'icon_direction_up_24' },
    ],
    { defaultValue: 'left' },
  );

const inputTitlePositionField = (
  path: string,
  defaultHeightLabelLeft: number,
  defaultHeightLabelTop: number,
  elementMarginPath = 'style.general.offset.margin',
): PropertiesConfigItemDefinition<'radios'> =>
  observedField(titlePositionField(path), (model: any, newPosition: string) => {
    const currentPosition = getValueByPath(model, path);
    if (currentPosition === newPosition) {
      return {};
    }

    const margin = getValueByPath<string | undefined>(model, elementMarginPath);
    if (!margin) {
      return {};
    }

    const height = ((model.root as any).height as HeightSettings).value || 0;
    const spacing = getDirectionValueMapFromCssProperty(margin);
    const spacingTop = toInt(spacing.top);
    const spacingBottom = toInt(spacing.bottom);
    const heightLabelTop = defaultHeightLabelTop + spacingTop + spacingBottom;
    const heightLabelLeft = defaultHeightLabelLeft + spacingTop + spacingBottom;

    if (newPosition === 'top') {
      return { 'root.height.value': heightLabelTop };
    }
    if (newPosition === 'left' && height <= heightLabelTop) {
      return { 'root.height.value': heightLabelLeft };
    }
    return {};
  });

const alignField = (
  path: string,
  label?: string,
  options?: PropertiesConfigInputOptionsAlign,
): PropertiesConfigItemDefinition<'align'> => ({
  path,
  label: label ?? 'Align',
  inputType: 'align',
  inputOptions: options,
});

const contentAlignField = (
  path: string,
  label: string,
  inputOptions: { hideLabel?: boolean; options?: Record<'label' | 'value', string>[] } = {
    hideLabel: false,
  },
): PropertiesConfigItemDefinition<'radios'> => ({
  path,
  label,
  inputType: 'radios',
  inputOptions: {
    renderType: 'text',
    defaultValue: 'left',
    options: [
      { label: 'Left', value: 'left' },
      { label: 'Right', value: 'right' },
    ],
    stretched: true,
    ...inputOptions,
  },
});

const imageAlignField = (
  path: string,
  label: string,
): PropertiesConfigItemDefinition<'radios'> => ({
  path,
  label,
  inputType: 'radios',
  inputOptions: {
    renderType: 'icon',
    options: [
      { label: 'Left', value: 'middle left', icon: 'icon_alignment_left_16' },
      { label: 'Center', value: 'middle center', icon: 'icon_alignment_center_16' },
      { label: 'Right', value: 'middle right', icon: 'icon_alignment_right_16' },
    ] satisfies SelectOptionWithIcon<ElementAlignment>[],
  },
});

const contentOverflowField = (
  path: string,
  label: string,
): PropertiesConfigItemDefinition<'radios'> => ({
  path,
  inputType: 'radios',
  label,
  inputOptions: {
    renderType: 'text',
    stretched: true,
    options: [
      { label: 'Scroll', value: 'auto' },
      { label: 'Clip', value: 'hidden' },
    ],
  },
});

const labelBehaviorField = (
  path: string,
  label: string,
): PropertiesConfigItemDefinition<'radios'> => ({
  path,
  inputType: 'radios',
  label,
  inputOptions: {
    renderType: 'icon-text',
    options: [
      { label: 'Left', value: 'left', icon: 'icon_direction_left_24' },
      { label: 'Top', value: 'top', icon: 'icon_direction_up_24' },
      { label: 'None', value: 'none' },
    ],
    defaultValue: 'top',
    stretched: true,
  },
});

const imageBehaviorField = (
  path: string,
  label = 'Image behavior',
  inputOptions?: Partial<PropertiesConfigInputOptionsRadios>,
): PropertiesConfigItemDefinition<'radios'> => {
  const options: SelectOption<ImageBehavior>[] = [
    { value: 'cover', label: 'Cover' },
    { value: 'contain', label: 'Fit' },
  ];

  return {
    path,
    label,
    inputType: 'radios',
    inputOptions: {
      renderType: 'text',
      stretched: true,
      options,
      ...inputOptions,
    },
  };
};

const actionsDisplayModeField = (
  path: string,
  label: string,
  inputOptions: Partial<PropertiesConfigInputOptionsRadios>,
  excludeOnHover = false,
): PropertiesConfigItemDefinition<'radios'> => ({
  path,
  inputType: 'radios',
  label,
  inputOptions: {
    renderType: 'text',
    options: !excludeOnHover
      ? [
          { label: 'Always', value: 'always' },
          { label: 'Collapsed', value: 'group' },
          { label: 'On Hover', value: 'hover' },
        ]
      : [
          { label: 'Always', value: 'always' },
          { label: 'Collapsed', value: 'group' },
        ],
    defaultValue: 'always',
    stretched: true,
    ...inputOptions,
  },
});

const genericSizeField = (
  path: string,
  label: string,
): PropertiesConfigItemDefinition<'radios'> => ({
  path,
  inputType: 'radios',
  label,
  inputOptions: {
    renderType: 'text',
    options: [
      { label: 'S', value: 'small' },
      { label: 'M', value: 'medium' },
      { label: 'L', value: 'large' },
    ],
    defaultValue: 'medium',
    stretched: true,
  },
});

const resizeBehaviorField = (
  path: string,
  label: string,
): PropertiesConfigItemDefinition<'radios'> => {
  const options: IconSelectOption<ResizeBehavior>[] = [
    { label: 'Auto', value: 'auto' },
    { label: 'Fixed', value: 'fixed' },
  ];

  return {
    path,
    inputType: 'radios',
    label,
    inputOptions: {
      renderType: 'text',
      options,
      defaultValue: 'medium',
      stretched: true,
    },
  };
};

//#endregion

//#region fields: ratio

const ratioField = (
  path: string,
  label: string,
  inputOptions?: PropertiesConfigInputOptionsRatio,
): PropertiesConfigItemDefinition<'ratio'> => ({
  path,
  label,
  inputType: 'ratio',
  inputOptions,
});

//#endregion

//#region fields: select

const selectField = (
  path: string,
  label: string,
  inputOptions: PropertiesConfigInputOptionsSelect,
): PropertiesConfigItemDefinition<'select'> => {
  if (!inputOptions.labelField) {
    inputOptions.labelField = 'label';
  }
  if (!inputOptions.valueField) {
    inputOptions.valueField = 'value';
  }

  return {
    path,
    label,
    inputType: 'select',
    inputOptions,
  };
};
const selectFieldFromList = (
  path: string,
  label: string,
  options: SelectOption[],
  inputOptions?: Partial<PropertiesConfigInputOptionsSelect>,
): PropertiesConfigItemDefinition<'select'> =>
  selectField(path, label, {
    labelField: 'label',
    valueField: 'value',
    ...inputOptions,
    dataSourceType: 'list',
    options,
  });
const selectFieldFromImageList = (
  imageListKind: SelectImageListKind,
  path: string,
  label: string,
  options: SelectOption[],
  inputOptions?: Partial<PropertiesConfigInputOptionsSelect>,
): PropertiesConfigItemDefinition<'select'> =>
  selectField(path, label, {
    labelField: 'label',
    valueField: 'value',
    ...inputOptions,
    dataSourceType: 'image-list',
    imageListKind,
    options,
  });
const selectFieldFromModel = (
  path: string,
  label: string,
  modelKey: string,
  inputOptions?: Partial<PropertiesConfigInputOptionsSelect>,
): PropertiesConfigItemDefinition<'select'> =>
  selectField(path, label, {
    labelField: 'label',
    valueField: 'value',
    ...inputOptions,
    dataSourceType: 'model',
    modelKey,
  });
const selectFieldFromApi = (
  path: string,
  label: string,
  selectorKey: PropertiesConfigInputOptionsSelectApiSelectors,
  inputOptions?: Partial<PropertiesConfigInputOptionsSelect>,
): PropertiesConfigItemDefinition<'select'> =>
  selectField(path, label, {
    labelField: 'label',
    valueField: 'value',
    ...inputOptions,
    dataSourceType: 'api',
    modelKey: selectorKey,
  });
const selectFieldFromDataBinding = (
  path: string,
  label: string,
  dataKey = 'value',
  inputOptions?: Partial<PropertiesConfigInputOptionsSelect>,
): PropertiesConfigItemDefinition<'select'> =>
  selectField(path, label, {
    labelField: 'label',
    valueField: 'value',
    showSelection: true,
    allowNone: false,
    ...inputOptions,
    dataSourceType: 'dataBindings',
    modelKey: dataKey,
  });
const selectFieldFromSchema = (
  path: string,
  label: string,
  dataSourcePath: string,
  inputOptions?: Partial<PropertiesConfigInputOptionsSelect>,
): PropertiesConfigItemDefinition<'select'> =>
  selectField(path, label, {
    labelField: 'name',
    valueField: 'id',
    showSelection: true,
    allowNone: false,
    ...inputOptions,
    dataSourceType: 'schema',
    modelKey: dataSourcePath,
  });
const selectDataSourceWithAlert = (
  path: string,
  label: string,
  inputOptions?: Partial<PropertiesConfigInputOptionsSelect>,
): PropertiesConfigItemDefinition<'select'> =>
  selectField(path, label, {
    hideLabel: true,
    labelField: 'name',
    valueField: 'id',
    showSelection: true,
    allowNone: false,
    ...inputOptions,
    dataSourceType: 'dataSourceWithAlert',
    layoutMode: 'datasource-selection',
  });
const selectFieldFromFormActions = (
  path: string,
  label: string,
  inputOptions?: Partial<PropertiesConfigInputOptionsSelect>,
): PropertiesConfigItemDefinition<'select'> =>
  selectField(path, label, {
    labelField: 'label',
    valueField: 'value',
    showSelection: true,
    allowNone: false,
    ...inputOptions,
    dataSourceType: 'formActions',
  });
const selectFieldFromFormFonts = (
  path: string,
  label = 'Font',
  inputOptions?: Partial<PropertiesConfigInputOptionsSelect>,
): PropertiesConfigItemDefinition<'select'> =>
  selectField(path, label, {
    labelField: 'name',
    valueField: 'value',
    showSelection: true,
    allowNone: true,
    showNoneOption: false,
    ...inputOptions,
    allowMultiSelect: false,
    dataSourceType: 'fonts',
  });
const selectFieldFromFontWeights = (
  path: string,
  label = 'Font Weight',
  relatedFontFamilyFieldPath = '',
  inputOptions?: Partial<PropertiesConfigInputOptionsSelect>,
): PropertiesConfigItemDefinition<'select'> =>
  selectField(path, label, {
    labelField: 'name',
    valueField: 'value',
    showSelection: true,
    allowNone: true,
    showNoneOption: false,
    modelKey: relatedFontFamilyFieldPath,
    defaultValue: NORMAL_FONT_WEIGHT,
    showDefaultValueWhenMissing: true,
    ...inputOptions,
    allowMultiSelect: false,
    dataSourceType: 'fontWeights',
  });

const selectFieldFromFormPages = (
  path: string,
  label = 'Page on click',
  inputOptions?: Partial<PropertiesConfigInputOptionsSelect>,
): PropertiesConfigItemDefinition<'select'> =>
  selectField(path, label, {
    labelField: 'label',
    valueField: 'value',
    showSelection: true,
    allowNone: true,
    ...inputOptions,
    allowMultiSelect: false,
    dataSourceType: 'api',
    modelKey: 'LIST_PAGES_WITH_MAINDATASET',
  });
const selectFieldFromStyleSheets = (
  path: string,
  label: string,
  inputOptions?: Partial<PropertiesConfigInputOptionsSelect>,
): PropertiesConfigItemDefinition<'select'> =>
  selectField(path, label, {
    labelField: 'label',
    valueField: 'value',
    showSelection: true,
    allowNone: true,
    placeholder: 'Select Style',
    ...inputOptions,
    dataSourceType: 'styleSheets',
  });
const selectFieldFromUsersColumns = (
  path: string,
  label: string,
  dataType?: ColumnDataType,
  inputOptions?: Partial<PropertiesConfigInputOptionsSelect>,
): PropertiesConfigItemDefinition<'select'> =>
  selectField(path, label, {
    labelField: 'label',
    valueField: 'value',
    showSelection: true,
    allowNone: true,
    ...inputOptions,
    dataSourceType: 'usersColumns',
    modelData: dataType,
  });

const sortField = (
  path: string,
  label: string,
  modelKey: string,
  labelField: string,
  valueField: string,
  inputOptions?: Partial<PropertiesConfigInputOptionsSelect>,
): PropertiesConfigItemDefinition<'select'> =>
  selectField(path, label, {
    ...inputOptions,
    dataSourceType: 'sort',
    modelKey,
    labelField,
    valueField,
  });

const selectDataSourceField = (
  bindingDef: PropertiesEditorDataSourceConfig,
  mode: DataSourceSelectMode = 'normal',
  label = '',
  inputOptions?: Partial<PropertiesConfigInputOptionsSelectDataSource>,
): PropertiesConfigItemDefinition<'selectDataSource'> => {
  return {
    path: 'data',
    label,
    inputType: 'selectDataSource',
    inputOptions: {
      bindingDef,
      mode,
      ...inputOptions,
    },
    dataBindings: [bindingDef],
  };
};

const selectDataValueField = (
  label: string,
  [path, ...modelPaths]: string[],
  modelData: (string | SelectOption[])[],
  inputOptions?: Partial<PropertiesConfigInputOptionsSelectDataValue>,
): PropertiesConfigItemDefinition<'selectDataValue'> => ({
  path,
  label,
  inputType: 'selectDataValue',
  inputOptions: {
    labelField: 'name',
    valueField: 'id',
    showSelection: true,
    allowNone: false,
    dataSourceType: 'schema',
    modelPaths,
    modelData,
    ...inputOptions,
  } as PropertiesConfigInputOptionsSelectDataValue,
});

const elementMetaDisplayValue =
  (path: string, property: ElementMetaUiFeaturesProps | 'title' | 'titleWidth') =>
  (elementId: CanvasElementId): Observable<string | undefined> => {
    const store = inject(Store);
    const elementDefinitionsService = inject(ElementDefinitionsService);
    const elementType$ = store.select(getElementById(elementId)).pipe(
      map((e) => e?.type),
      distinctUntilChanged(),
    );
    const propValue$ = store.select(getElementById(elementId)).pipe(
      map((element) => {
        if (!hasProps(element)) {
          return undefined;
        }
        return getValueByPath(element.props, path);
      }),
      distinctUntilChanged(),
    );
    return store.select(runtimeSelectors.selectElementMetaById(elementId)).pipe(
      combineLatestWith(elementType$, propValue$),
      map(
        ([meta, elementType, propValue]) =>
          resolveElementMetaDisplayValue(
            path,
            property,
            meta,
            elementDefinitionsService.getDefinition(elementType)?.metaUiFeatures?.paths,
          ) ??
          (propValue as string | undefined) ??
          '',
      ),
      distinctUntilChanged(),
      countedShareReplay(),
    );
  };

const barcodeScannerFormatField = (
  path: string,
  label: string,
): PropertiesConfigItemDefinition<'select'> =>
  selectFieldFromList(
    path,
    label,
    [
      { value: 'text', label: 'Text' },
      { value: 'paragraph', label: 'Text Paragraph' },
      { value: 'richText', label: 'Rich Text' },
    ],
    {
      allowNone: false,
      showSelection: true,
      displayValue: elementMetaDisplayValue(path, 'format'),
    },
  );

const fontFamilyAndWeightFields = (
  fontFamilyPath: string,
  fontWeightPath: string,
): PropertiesConfigItemDefinition[] => [
  selectFieldFromFormFonts(fontFamilyPath, 'Font'),
  selectFieldFromFontWeights(fontWeightPath, 'Weight', fontFamilyPath),
];

const numberPrecisionField = (
  path: string,
  label = 'Precision',
  inputOptions?: Partial<PropertiesConfigInputOptionsSelect>,
): PropertiesConfigItemDefinition<'select'> =>
  selectFieldFromList(path, label, PRECISION_OPTIONS, {
    showSelection: true,
    allowNone: true,
    ...inputOptions,
    displayValue: elementMetaDisplayValue(path, 'precision'),
  });

const currencyConditionalField = (
  path: string,
  inputFormatPath: string,
  inputOptions?: Partial<PropertiesConfigInputOptionsSelect>,
  label = 'Currency',
): PropertiesConfigItemDefinition<'select'> =>
  conditionalField(
    selectFieldFromList(path, label, CURRENCY_OPTIONS, {
      showSelection: true,
      allowNone: true,
      ...inputOptions,
      displayValue: elementMetaDisplayValue(path, 'currency'),
    }),
    inputFormatPath,
    (value) => value?.toLowerCase() !== 'currency',
  );

const durationFormatField = (
  path: string,
  label = 'Duration format',
  inputOptions?: Partial<PropertiesConfigInputOptionsSelect>,
): PropertiesConfigItemDefinition<'select'> =>
  selectFieldFromList(path, label, DURATION_FORMAT_OPTIONS, {
    showSelection: true,
    allowNone: true,
    ...inputOptions,
    displayValue: elementMetaDisplayValue(path, 'duration'),
  });

const dateFormatField = (
  path: string,
  label = 'Date format',
  inputOptions?: Partial<PropertiesConfigInputOptionsSelect>,
): PropertiesConfigItemDefinition<'select'> =>
  selectFieldFromList(path, label, DATE_FORMAT_OPTIONS, {
    showSelection: true,
    allowNone: true,
    defaultValue: '',
    ...inputOptions,
    displayValue: elementMetaDisplayValue(path, 'dateFormat'),
  });

const dateTimeFormatField = (
  path: string,
  label = 'Date Time format',
  inputOptions?: Partial<PropertiesConfigInputOptionsSelect>,
): PropertiesConfigItemDefinition<'select'> =>
  selectFieldFromList(path, label, DATETIME_FORMAT_OPTIONS, {
    showSelection: true,
    allowNone: true,
    defaultValue: '',
    fullWidthDropdown: true,
    ...inputOptions,
    displayValue: elementMetaDisplayValue(path, 'dateTimeFormat'),
  });

const textInputFormatField = (
  path: string,
  label: string,
): PropertiesConfigItemDefinition<'select'> =>
  selectFieldFromList(path, label, TEXT_INPUT_FORMATS, {
    allowNone: false,
    showSelection: true,
    displayValue: elementMetaDisplayValue(path, 'format'),
  });

const textFieldFormatField = (
  path: string,
  label: string,
): PropertiesConfigItemDefinition<'select'> =>
  selectFieldFromList(path, label, TEXT_FIELD_FORMATS, {
    showSelection: true,
    displayValue: elementMetaDisplayValue(path, 'format'),
  });

const visualEffectTypeField = (
  path: string,
  label: string,
  options = VISUAL_EFFECT_OPTIONS,
): PropertiesConfigItemDefinition<'select'> => {
  const inputOpts: Partial<PropertiesConfigInputOptionsSelect> = {
    showSelection: true,
    layoutMode: 'normal',
    hideLabel: true,
    defaultValue: 'none',
    showNoneOption: false,
    disableDetailsAction: (value) => value === 'none',
    detailsAction: {
      key: 'VISUAL_EFFECT_EDIT',
      paneTitle: '',
      config: () => [subsection(path, '', [], { hideLabel: true })],
      propPath: path,
    },
  };

  return selectFieldFromList(`${path}.type`, label, options, inputOpts);
};

const visualEffectField = (
  path: string,
  options?: SelectOption<VisualEffectType>[],
): PropertiesConfigItemDefinition[] => [
  separator('Effect'),
  observedField(
    visualEffectTypeField(path, 'Effect', options),
    (model, value: VisualEffectType) => {
      if (!value || value === 'none') {
        return { [path]: undefined };
      }

      const data = getDefaultVisualEffectDataForType(value);

      if (!data) {
        return {};
      }

      return { [`${path}.data`]: data };
    },
  ),
];

//#endregion

//#region fields: size

interface SizeFieldOptions {
  path: string;
  label: string;
  units: PropertiesConfigInputOptionsSliderUnitConfig[];
  sizingOptions?: SelectOption<ElementSizingMode>[];
  optionsFilter?: (
    model: unknown,
    options: SelectOption<ElementSizingMode>[],
  ) => SelectOption<ElementSizingMode>[];
  displayValue?: PropsDisplayValueFn;
  showArrows?: boolean;
  labelWidth?: string;
}

const sizeField = ({
  path,
  label,
  units,
  sizingOptions,
  optionsFilter,
  displayValue,
  showArrows,
  labelWidth,
}: SizeFieldOptions): PropertiesConfigItemDefinition<'size'> => ({
  path,
  label,
  inputType: 'size',
  inputOptions: {
    units,
    options: sizingOptions,
    optionsFilter,
    displayValue,
    showArrows,
    labelWidth,
  },
});

function getElementSizeDisplayValueFn(dim: 'width' | 'height') {
  return (elementId: CanvasElementId): Observable<any> => {
    const store = inject(Store);
    return store.select(getElementById(elementId)).pipe(
      switchMap((element) => {
        if (!hasProps(element)) {
          return of(undefined);
        }
        const props = element.props;
        const elementType = element.type;
        const itemModel = getValueByPath<HeightSettings | WidthSettings>(element, dim);
        if (arePropsWithDirty(props) && props.dirty?.[dim]) {
          return of(itemModel);
        }
        if (arePropsWithData(props)) {
          const binding = getActiveBinding(
            props.data,
            getFormInputNameByType(elementType as CanvasElementType),
          );
          if (binding?.dataSource?.sourceType === 'formContainer') {
            return store.select(getElementById(binding.dataSource.id)).pipe(
              take(1),
              map((form) => {
                if (!hasProps(form) || !isVariantOfFormContainer(form)) {
                  return itemModel;
                }

                const override = form.props?.content?.children?.size?.[dim];
                return override ? { ...itemModel, ...override } : itemModel;
              }),
            );
          }
        }
        return of(itemModel);
      }),
    );
  };
}

/** element's width knob */
const elementWidthField = (
  unit: PropertiesConfigInputOptionsSliderUnitConfig,
  sizingOptions?: ElementSizingMode[],
  optionsFilter?: (
    model: unknown,
    options: SelectOption<ElementSizingMode>[],
  ) => SelectOption<ElementSizingMode>[],
): PropertiesConfigItemDefinition<'size'> => {
  const options = sizingOptions?.map((o) => ({
    value: o,
    label: toTitleCase(o),
  }));
  return sizeField({
    path: 'root.width',
    label: 'Width',
    units: [unit],
    sizingOptions: options,
    optionsFilter,
    displayValue: getElementSizeDisplayValueFn('width'),
  });
};

/** element's height knob */
const elementHeightField = (
  unit: PropertiesConfigInputOptionsSliderUnitConfig,
  sizingOptions?: ElementSizingMode[],
  optionsFilter?: (
    model: unknown,
    options: SelectOption<ElementSizingMode>[],
  ) => SelectOption<ElementSizingMode>[],
  label?: string,
  labelWidth?: string,
): PropertiesConfigItemDefinition<'size'> => {
  const options = sizingOptions?.map((o) => ({
    value: o,
    label: toTitleCase(o),
  }));
  return sizeField({
    path: 'root.height',
    label: label ?? 'Height',
    units: [unit],
    sizingOptions: options,
    optionsFilter,
    displayValue: getElementSizeDisplayValueFn('height'),
    labelWidth,
  });
};

const columnSizeField = (
  path: string,
  label: string,
): PropertiesConfigItemDefinition<'columnSize'> => ({
  path,
  label,
  inputType: 'columnSize',
});

//#endregion

//#region fields: slider

const sliderField = (
  path: string,
  label: string,
  unitsOrOptions:
    | PropertiesConfigInputOptionsSliderUnitConfig[]
    | PropertiesConfigInputOptionsSlider,
): PropertiesConfigItemDefinition<'slider'> => ({
  path,
  label,
  inputType: 'slider',
  inputOptions: Array.isArray(unitsOrOptions) ? { units: unitsOrOptions } : unitsOrOptions,
});

const radiusField = (
  path: string,
  label?: string,
  inputOptions?: PropertiesConfigInputOptionsRadius,
): PropertiesConfigItemDefinition<'radius'> => ({
  path,
  label: label || 'Radius',
  inputType: 'radius',
  inputOptions: { allowPerCorner: true, ...inputOptions },
});

//#endregion

//#region fields: subConfig

const subConfigField = (
  path: string,
  label: string,
  options: SetOptional<PropertiesConfigInputOptionsSubConfig, 'buttonLabel'>,
): PropertiesConfigItemDefinition<'subConfig'> => {
  return {
    inputType: 'subConfig',
    path,
    label,
    inputOptions: {
      ...options,
      buttonLabel: options.buttonLabel ?? 'Edit',
    },
  };
};

//#endregion

//#region fields: remoteText

/** Text edited on another element, addressed by an id kept in the selected element's props. */
const remoteTextField = (
  path: string,
  label: string,
  options: PropertiesConfigInputOptionsRemoteText,
): PropertiesConfigItemDefinition<'remoteText'> => ({
  inputType: 'remoteText',
  path,
  label,
  inputOptions: options,
});

//#endregion

//#region fields: tagsColorPalette

const tagsColorPaletteField = (
  path: string,
  label = '',
  inputOptions?: Partial<PropertiesConfigInputOptionsTagsColorPalette>,
): PropertiesConfigItemDefinition<'tagsColorPalette'> => ({
  path,
  label,
  inputType: 'tagsColorPalette',
  inputOptions: {
    palettes: PALETTE_OPTIONS,
    hideLabel: false,
    bindingKey: 'value',
    ...inputOptions,
  },
});

//#endregion

//#region fields: text

const textInput = (
  path: string,
  label: string,
  inputOptions?: PropertiesConfigInputOptionsText,
): PropertiesConfigItemDefinition<'text'> => ({
  path,
  label,
  inputType: 'text',
  inputOptions,
});

const titleField = (path: string): PropertiesConfigItemDefinition<'text'> =>
  textInput(path, 'Title');

//#endregion

//#region fields: textInfo

const textInfo = (
  path: string,
  label: string,
  inputOptions?: PropertiesConfigInputOptionsTextInfo,
): PropertiesConfigItemDefinition<'textInfo'> => ({
  path,
  label,
  inputType: 'textInfo',
  inputOptions,
});

//#endregion

//#region fields: textArea

const textAreaField = (
  path: string,
  label = '',
  inputOptions?: PropertiesConfigInputOptionsTextArea,
): PropertiesConfigItemDefinition<'textArea'> => ({
  path,
  label,
  inputType: 'textArea',
  inputOptions,
});

//#endregion

//#region fields: timeRange

const timeRangeField = (
  path: string,
  label: string,
  inputOptions?: Partial<PropertiesConfigInputOptionsTimeRange>,
): PropertiesConfigItemDefinition<'timeRange'> => ({
  path,
  label,
  inputType: 'timeRange',
  inputOptions,
});

//#endregion

//#region fields: toggle

const toggleField = (
  path: string,
  label: string,
  options?: PropertiesConfigInputOptionsToggle,
): PropertiesConfigItemDefinition<'toggle'> => ({
  path,
  label,
  inputType: 'toggle',
  inputOptions: options,
});

//#endregion

//#region fields: toggleArray

const toggleArrayField = (
  path: string,
  label: string,
  options: (model: any) => IconSelectOption[],
  inputOptions?: { searchEnabled?: boolean; selectAllEnabled?: boolean },
): PropertiesConfigItemDefinition<'toggleArray'> => ({
  path,
  inputType: 'toggleArray',
  label,
  inputOptions: {
    ...inputOptions,
    options,
  },
});

//#endregion

//#region fields: upload

const uploadField = (
  path: string,
  label: string,
  inputOptions: PropertiesConfigInputOptionsUpload = {},
): PropertiesConfigItemDefinition<'upload'> => ({
  path,
  label,
  inputType: 'upload',
  inputOptions,
});

//#endregion

//#region fields: wysiwygArea

const wysiwygAreaField = (
  path: string,
  label = '',
  inputOptions?: PropertiesConfigInputOptionsWysiwygArea,
): PropertiesConfigItemDefinition<'wysiwygArea'> => ({
  path,
  label,
  inputType: 'wysiwygArea',
  inputOptions,
});

//#endregion

//#region Container fields & actions

const listConditionalFormattingField = (
  path: string,
): PropertiesConfigItemDefinition<'listConditionalFormatting'> => ({
  path,
  label: '',
  inputType: 'listConditionalFormatting',
});

const buttonActionField = (
  path: string,
  label = 'Action',
  inputOptions?: Partial<PropertiesConfigInputOptionsContainerAction>,
): PropertiesConfigItemDefinition<'containerAction'> => ({
  label,
  inputType: 'containerAction',
  path,
  inputOptions: {
    displayMode: 'trigger-button',
    actionConfig: () => actionConfigSections(path),
    ...inputOptions,
  },
});

const buttonVisibleField = (
  path: string,
  inputOptions?: Partial<PropertiesConfigInputOptionsContainerVisibility>,
): PropertiesConfigItemDefinition<'containerVisibility'> => {
  return {
    path,
    label: 'Visibility',
    inputType: 'containerVisibility',
    inputOptions: {
      actionConfig: () => path,
      ...inputOptions,
    } as any,
  };
};

const containerItemActionsField = (
  path: string,
  label: string,
  options?: {
    generateActionsIds?: boolean;
    simpleButtonConfig?: boolean;
    actionPaneTitle?: string;
    actionInitialValues?: Partial<PropertiesConfigInputContainerItemActionModel>;
    actionGenerationType?: ActionKind;
    actionEventType?: RuntimeEventType;
    skipVisibility?: boolean;
  },
): PropertiesConfigItemDefinition<'containerItemActions'> => ({
  path,
  inputType: 'containerItemActions',
  label,
  inputOptions: {
    showActionLabelDefault: true,
    generateActionsIds: options?.generateActionsIds ?? false,
    actionPaneTitle: options?.actionPaneTitle,
    actionInitialValues: options?.actionInitialValues,
    actionConfig: (index: number) =>
      actionConfigSections(`${path}.${index}.action`, {
        buttonPath: `${path}.${index}`,
        showIconOnlyToggle: !options?.simpleButtonConfig,
        showIconPositionToggle: !options?.simpleButtonConfig,
        actionGenerationType: options?.actionGenerationType,
        eventType: options?.actionEventType,
        skipVisibility: options?.skipVisibility ?? false,
      }),
  },
});

const containerRowClickActionsField = (
  label: string,
  editLabel = 'Edit row',
  inputOptions?: Partial<PropertiesConfigInputOptionsSelect>,
): PropertiesConfigItemDefinition[] => [
  toggleField('content.rowClick.enabled', label),
  conditionalField(
    selectFieldFromList(
      'content.rowClick.action',
      '',
      [
        { label: 'Custom Action', value: ITEMS_CONTAINER_ACTION_ROW_CLICK },
        { label: 'View Details', value: ITEMS_CONTAINER_ACTION_VIEW },
        { label: editLabel, value: ITEMS_CONTAINER_ACTION_EDIT },
      ],
      {
        hideLabel: true,
        allowNone: false,
        placeholder: 'Select action',
        layoutMode: 'datasource-subselection',
        disableDetailsAction: (value) => value !== ITEMS_CONTAINER_ACTION_ROW_CLICK,
        detailsAction: {
          paneTitle: '',
          key: 'COLLECTION_EDIT_ACTION',
          config: () => {
            return [subsection('', '', [])];
          },
          type: {
            [ITEMS_CONTAINER_ACTION_ROW_CLICK]: 'blaze-action-button',
            [ITEMS_CONTAINER_ACTION_VIEW]: 'link-edit-button',
            [ITEMS_CONTAINER_ACTION_EDIT]: 'link-edit-button',
          },
          workflowId: (model: unknown) => {
            const rowClick = (model as PropsOf<'table'>)?.content.rowClick;
            const targetAction = rowClick?.action;
            if (targetAction && rowClick?.targetId?.[targetAction]) {
              if (typeof rowClick.targetId[targetAction] === 'string') {
                return rowClick.targetId[targetAction] as string;
              } else if ('targetId' in (rowClick.targetId[targetAction] as ElementRedirect)) {
                return (rowClick.targetId[targetAction] as ElementRedirect)?.targetId;
              }
            }
            return undefined;
          },
          action: (_model: unknown) => {
            return { id: ITEMS_CONTAINER_ACTION_ROW_CLICK };
          },
          hideActionForNone: true,
        },
        ...inputOptions,
      },
    ),
    'content.rowClick.enabled',
    (value) => !value,
  ),
];

const unifiedActionsField = (
  path: string,
  group: UnifiedActionsGroup,
  options: {
    hasWorkflow?: (action: PropertiesConfigInputContainerItemUnifiedActionModel<any>) => boolean;
    workflowIdPath?: (
      action: PropertiesConfigInputContainerItemUnifiedActionModel<any>,
    ) => string | undefined;
  } = {},
): PropertiesConfigItemDefinition<'unifiedActions'> => ({
  path,
  inputType: 'unifiedActions',
  label: '',
  inputOptions: {
    group,
    actionConfig: (index: number) =>
      actionConfigSections(`${path}.${index}.action`, {
        buttonPath: `${path}.${index}`,
        showIconOnlyToggle: true,
        showIconPositionToggle: true,
        skipVisibility: true,
        actionGenerationType: group,
        hasWorkflow: options.hasWorkflow,
        workflowIdPath: options.workflowIdPath,
      }),
  },
});

const linkFields = (): PropertiesConfigItemDefinition[] => [
  separatedField(radiosField('content.link.action', 'Link to', NavMenu.LINK_ACTIONS), {
    fullWidthSeparator: true,
  }),
  // Link: Page
  conditionalField(
    selectFieldFromApi(`content.link.navigatePageId`, '', 'LIST_PAGES', {
      showNoneOption: false,
      showSelection: true,
      placeholder: 'Select page',
      hideLabel: true,
    }),
    'content.link.action',
    (action: LinkAction) => action !== 'GoToPage',
  ),
  // Link: URL
  conditionalField(
    textInput('content.link.navigateUrl', '', { hideLabel: true, placeholder: 'Copy/paste URL' }),
    'content.link.action',
    (action: LinkAction) => action !== 'Url',
  ),
];

const listFieldsField = (
  path: string,
  dataSourcePath: string,
  noFields?: boolean,
  bindingKey?: CanvasElementInputBinding,
): PropertiesConfigItemDefinition<'listFields'> => ({
  path,
  inputType: 'listFields',
  label: '',
  inputOptions: {
    dataSourcePath,
    noFields,
    bindingKey,
  },
});

type ListCustomFieldsFieldOptions = {
  withLabel: boolean;
  withLabelPosition: boolean;
  /** @deprecated - Use `source = 'schema'` instead */
  source: 'model' | 'schema';
  datasourcePath: string;
  /** Whether to use the display value for the label from meta features */
  useMetaValue: boolean;
};

const listCustomFieldsField = (
  path: string,
  label: string,
  {
    withLabel = true,
    withLabelPosition = true,
    /** @deprecated - Use `source = 'schema'` instead */
    source = 'model',
    datasourcePath = 'data.?key:value.dataSource',
    useMetaValue = true,
  }: Partial<ListCustomFieldsFieldOptions> = {},
): PropertiesConfigItemDefinition<'collectionList'> => {
  const selectFieldInputOptions = {
    labelField: 'name',
    valueField: COLUMN_ID_OR_NAME,
    allowNone: false,
    showSelection: true,
    optionsFilter: columnFormatFilter('!secureText'),
  };

  const makeFormatHiddenPredicate =
    (useMetaValue: boolean, ...formats: string[]) =>
    (
      displayFormat: string,
      _: unknown,
      { fieldFormat: format }: { fieldFormat: string | undefined },
    ): boolean =>
      formats.includes(useMetaValue ? (format ?? '') : displayFormat);

  const makeFormatShownPredicate =
    (useMetaValue: boolean, ...formats: string[]) =>
    (
      displayFormat: string,
      _: unknown,
      { fieldFormat: format }: { fieldFormat: string | undefined },
    ): boolean =>
      !formats.includes(useMetaValue ? (format ?? '') : displayFormat);

  return {
    path,
    inputType: 'collectionList',
    label,
    inputOptions: {
      fieldAction: 'COLLECTION_EDIT_FIELD',
      fieldPaneTitle: 'Custom Field',
      fieldConfig: (index): PropertiesConfigSection[] | PropertiesConfigSubsection[] => {
        const relatedFormatPath = useMetaValue
          ? `${path}.${index}.format`
          : `${path}.${index}.displayFormat`;

        const sourceField =
          source === 'model'
            ? selectFieldFromModel(
                `${path}.${index}.column`,
                'Field',
                'content.columns',
                selectFieldInputOptions,
              )
            : selectFieldFromSchema(
                `${path}.${index}.column`,
                'Field',
                datasourcePath,
                selectFieldInputOptions,
              );

        return [
          subsection(
            `${path}.${index}`,
            '',
            [
              observedField(sourceField, (model, value) => {
                if (!useMetaValue) {
                  const activeBinding = getActiveBinding(model.data, 'optionsList');
                  if (activeBinding?.dataSource?.sourceType === 'context') {
                    const schemaService = inject(SchemaService);
                    const schema = schemaService.getSchema(
                      activeBinding?.dataSource?.id ?? '',
                      true,
                    );
                    const column = findColumn(schema?.columns, value);
                    if (!column) {
                      return {};
                    }
                    const result: Record<string, any> = {};
                    result[`${path}.${index}.displayFormat`] = getFormatByDataType(
                      column.dataType,
                      column.format?.formatType,
                    );
                    if (column.precision) {
                      result[`${path}.${index}.precision`] = column.precision;
                    }
                    if (column.format?.currencySymbol) {
                      result[`${path}.${index}.currency`] = column.format?.currencySymbol;
                    }
                    if (!getValueByPath(model, `${path}.${index}.label`)) {
                      result[`${path}.${index}.label`] = column.name;
                    }

                    return result;
                  }
                } else {
                  const columns = getValueByPath<PropertiesConfigInputListColumnModel[]>(
                    model,
                    'content.columns',
                  );
                  const column = findColumn(columns, value);
                  if (!column) {
                    return {};
                  }
                  const field = getValueByPath<PropertiesConfigInputListCustomFieldModel>(
                    model,
                    `${path}.${index}`,
                  );
                  const displayFormat = column.format || field?.displayFormat;
                  const updatedField = { ...field, displayFormat };

                  if (column.precision !== undefined) {
                    updatedField.precision = column.precision;
                  } else {
                    delete updatedField.precision;
                  }
                  if (column.currency !== undefined) {
                    updatedField.currency = column.currency;
                  } else {
                    delete updatedField.currency;
                  }

                  const isDirtyLabel: boolean =
                    'dirty' in updatedField &&
                    !!updatedField.dirty &&
                    'label' in (updatedField.dirty as object);

                  if (column.name && !isDirtyLabel) {
                    updatedField.label = column.name;
                  }
                  return { [`${path}.${index}`]: updatedField };
                }
                return {};
              }),
              conditionalField(
                selectFieldFromList(`${path}.${index}.displayFormat`, 'Type', LIST_COLUMN_FORMATS, {
                  showSelection: true,
                  defaultValue: 'text' as TableColumnFormat,
                  displayValue: useMetaValue
                    ? elementMetaDisplayValue(`${path}.${index}.displayFormat`, 'format')
                    : undefined,
                }),
                relatedFormatPath,
                makeFormatHiddenPredicate(useMetaValue, 'secureText', 'geopoint'),
              ),
              conditionalField(
                numberPrecisionField(`${path}.${index}.precision`, 'Precision', {
                  defaultValue: null,
                }),
                relatedFormatPath,
                makeFormatShownPredicate(useMetaValue, 'number', 'percentage', 'currency'),
              ),
              currencyConditionalField(
                `${path}.${index}.currency`,
                `${path}.${index}.displayFormat`,
                {
                  defaultValue: 'USD',
                },
              ),
              conditionalField(
                durationFormatField(`${path}.${index}.duration`, 'Duration format', {
                  defaultValue: 'h:mm',
                }),
                relatedFormatPath,
                makeFormatShownPredicate(useMetaValue, 'duration'),
              ),
              conditionalField(
                dateFormatField(`${path}.${index}.dateFormat`, 'Date format'),
                relatedFormatPath,
                makeFormatShownPredicate(useMetaValue, 'date'),
              ),
              conditionalField(
                dateTimeFormatField(`${path}.${index}.dateTimeFormat`, 'Date Time format'),
                relatedFormatPath,
                makeFormatShownPredicate(useMetaValue, 'datetime'),
              ),
              ...(withLabel
                ? [
                    textInput(`${path}.${index}.label`, 'Label', {
                      displayValue: useMetaValue
                        ? elementMetaDisplayValue(`${path}.${index}.label`, 'title')
                        : undefined,
                    }),
                  ]
                : []),
              ...(withLabelPosition
                ? [labelBehaviorField(`${path}.${index}.labelBehavior`, 'Label position')]
                : []),
              conditionalField(
                tagsColorPaletteField(`${path}.${index}.tagsPalette`, 'Palette', {
                  dataSourceKey: `${path}.${index}.column`,
                  hideLabel: false,
                  defaultValue: { ...DEFAULT_COLUMN_TAGS_PALETTE },
                }),
                `${path}.${index}`,
                (field: PropertiesConfigInputListCustomFieldModel | undefined) => {
                  return field?.displayFormat !== 'tag';
                },
              ),
            ],
            { hideLabel: true, labelWidth: '35%' },
          ),
        ];
      },
      fieldFactory: (): PropertiesConfigInputListCustomFieldModel => ({
        label: '',
        labelBehavior: 'top',
      }),
      listLabelPaths: useMetaValue ? ['label', 'column'] : ['label'],
      hasVisibility: true,
      avoidDisplayValueForLabel: !useMetaValue,
    },
  };
};

const listItemActionsField = (
  path: string,
  label: string,
): PropertiesConfigItemDefinition<'collectionList'> => ({
  path,
  inputType: 'collectionList',
  label,
  inputOptions: {
    fieldAction: 'COLLECTION_EDIT_ACTION',
    fieldPaneTitle: 'Action',
    fieldConfig: (index) =>
      actionConfigSections(`${path}.${index}.action`, {
        buttonPath: `${path}.${index}`,
        showIconPositionToggle: true,
        skipVisibility: true,
      }),
    fieldFactory: (): PropertiesConfigInputContainerItemActionModel => ({
      label: '',
    }),
    hasVisibility: true,
    iconField: 'icon',
  },
});

const listTableGroupByFields = (
  path: string,
  label: string,
  dataSourcePath: string,
): PropertiesConfigItemDefinition<'collectionList'> => ({
  path,
  inputType: 'collectionList',
  label,
  inputOptions: {
    disableDrag: true,
    fieldFactory: (): { field?: string } => ({
      field: undefined,
    }),
    childKnobFactory: (index, model): PropertiesConfigItemDefinition<'text' | 'select'> => {
      if (index === 0) {
        return textInput(`content.table.groupByColumn.label`, 'Column name', {
          hideWhen: (model) => model.content.table.groupBy.length < 2,
        });
      }
      return observedField(
        selectFieldFromSchema(`${path}.${index}.field`, `Level ${index}`, dataSourcePath, {
          layoutMode: 'groupby-select',
        }),
        (_, value, __, ___, ____, path) => {
          const result: Record<string, string | boolean> = {};

          // reorder when needed
          for (const [i, el] of model.content.table.groupBy?.entries() || []) {
            if (i === 0) {
              continue;
            }
            if (el.field === value && index !== i) {
              result[`content.table.groupBy.${i}.field`] = model.content.table.groupBy[index].field;
            }
          }

          // hide selected col in columns section
          model.content.columns.forEach((el: Record<string, any>, i: number) => {
            if (el.id === value) {
              result[`content.columns.${i}.visible`] = false;
            }
          });

          // turn off pagination
          if (value && path.endsWith('field') && model.content.table.usePagination) {
            result['content.table.usePagination'] = false;
          }

          return result;
        },
      );
    },
    listLabelPaths: ['level'],
    fieldAction: 'TABLE_EDIT_ACTION',
    fieldConfig: () => [],
    fieldPaneTitle: '',
    fieldActionFactory: () => false,
    omitFieldOpenAfterAdd: true,
    itemTemplate: ['text', 'select', 'select', 'select'],
    actionsAlwaysVisible: true,
    fieldRemoveFactory: (item, index) => index !== 0,
    fieldsCountAtStart: 2,
  },
});

const listChatIntroMessagesFields = (
  path: string,
  label: string,
): PropertiesConfigItemDefinition<'collectionList'> => ({
  path,
  inputType: 'collectionList',
  label,
  inputOptions: {
    disableDrag: false,
    actionsAlwaysVisible: false,
    fieldAction: 'CHAT_EDIT_INTRO_MESSAGES',
    fieldPaneTitle: 'Message Details',
    itemTemplate: 'sanitizedHtml',
    fieldConfig: (index) => [
      subsection(
        `${path}.${index}`,
        '',
        [wysiwygAreaField(`${path}.${index}.message`, 'Message', { hideLabel: true })],
        { hideLabel: true },
      ),
    ],
    fieldFactory: ({
      id,
    }: PropertiesConfigInputConnectionListFactoryMetadata): { id: number; message: string } => {
      return {
        id,
        message: '',
      };
    },
    childKnobFactory: (index) =>
      subsection(
        `${path}.${index}`,
        '',
        [wysiwygAreaField(`${path}.${index}.message`, 'Message', { hideLabel: true })],
        { hideLabel: true },
      ),
    placeholderFactory: ({ _, message }: Record<string, unknown>): string => {
      return typeof message === 'string' ? message : '';
    },
    listLabelPaths: ['message'],
    fieldActionFactory: () => true,
    fieldRemoveFactory: () => true,
  },
});

const listChartFields = (
  path: string,
  label: string,
  dataSourceOptions: SelectOption<ChartAggregateFunctionType, string>[],
  additionalInputOptions?: {
    hideAddButton?: boolean;
    preventListAction?: boolean;
  },
): PropertiesConfigItemDefinition<'collectionList'> => ({
  path,
  inputType: 'collectionList',
  label,
  inputOptions: {
    disableDrag: true,
    hideAddButton: additionalInputOptions?.hideAddButton,
    actionsAlwaysVisible: true,
    fieldAction: 'CHARTS_EDIT_SERIES',
    fieldPaneTitle: 'Y Axis series',
    itemTemplate: 'selectDataValue',
    fieldConfig: (index, model, fields) => [
      subsection(
        `data.?key:value.enabled`,
        '',
        [
          textInput(index === 0 ? 'content.data.alias' : `${path}.${index}.alias`, 'Series Name', {
            placeholderFactory: (model) => {
              const schemaService = inject(SchemaService);
              const propertyField =
                getValueByPath<string>(
                  model,
                  index === 0 ? 'content.data.mainValue' : `${path}.${index}.mainValue`,
                ) ?? undefined;
              const propertyAggregation =
                getValueByPath<string>(
                  model,
                  index === 0 ? 'content.data.aggregation' : `${path}.${index}.aggregation`,
                ) ?? undefined;
              if (propertyField) {
                const fieldName = schemaService.getColumnName(propertyField);
                return createAliasForAggregation({}, index + 1, fieldName, propertyAggregation);
              }

              return createAliasForAggregation({}, index + 1, undefined, undefined);
            },
          }),
          observedField(
            selectDataValueField(
              'Data',
              [
                index === 0 ? 'content.data.aggregation' : `${path}.${index}.aggregation`,
                index === 0 ? 'content.data.mainValue' : `${path}.${index}.mainValue`,
              ],
              ((): (string | SelectOption<ChartAggregateFunctionType, string>[])[] => {
                const usedAggregation = getValueByPath(model, 'content.data.aggregation');
                const fieldsFromFieldFactoryAfterMainAggregations = fields?.filter(
                  (v) => 'id' in v,
                );
                return [
                  fieldsFromFieldFactoryAfterMainAggregations?.length === 0
                    ? dataSourceOptions
                    : usedAggregation === 'FIELD VALUE'
                      ? dataSourceOptions.filter((a) => a.value === 'FIELD VALUE')
                      : dataSourceOptions.filter((a) => a.value !== 'FIELD VALUE'),
                  'data.?key:value.dataSource',
                ];
              })(),
              {
                dataBindingsMode: 'first-dataset',
                allowSubSelection: true,
                filterColumnsType: ['Formula', 'Number'],
                emptyListPlaceholder: 'No numeric fields',
                // prevent Selecting Column For Count
                allowOpenStepsPredicate: (
                  option: SchemaColumnSelectOption,
                  index: number,
                ): boolean => {
                  if (option.value === 'COUNT' && index === 0) {
                    return false;
                  }
                  return true;
                },
                backBtnLabel: 'Back to Aggregation Methods',
                stepsTagViewUse: [true],
                // is Compute Node For Chart
                nodePredicate: (node: WorkflowNode): boolean => {
                  return (
                    !!findWorkflowNode([node], isAggregateNode, 'aggregate', 'chart-') ||
                    !!findWorkflowNode([node], isComputeNode, undefined, 'chart-')
                  );
                },
                placeholder: 'Select',
              },
            ),
            (model: ChartPropsModel, value: string) => {
              const result: Record<string, string> = {};
              if (index === 0) {
                if (model.content.data?.groupBy === value) {
                  result['content.data.groupBy'] = model.content.data?.mainValue || '';
                } else if (model.content.data?.segmentData === value) {
                  result['content.data.segmentData'] = model.content.data?.mainValue || '';
                } else if (value === 'COUNT') {
                  result['content.data.mainValue'] = '';
                }
              } else if (value === 'COUNT') {
                result[`${path}.${index}.mainValue`] = '';
              }
              return result;
            },
          ),
          colorField(`${path}.${index}.color`, 'Color', {
            defaultValue: {
              color1: ((): string => {
                const themePalette = inject(ThemesService).activeTheme?.value.palettes['palette2']
                  .bg as string[];
                const paletteLength = themePalette?.length || 1;

                return themePalette[index % paletteLength];
              })(),
            },
            allowGradient: false,
            allowNone: false,
            hideWhen: (model) => model.root.elementType === 'chartLine',
          }),
        ],
        { hideLabel: true },
      ),
    ],
    fieldFactory: ({
      id,
    }: PropertiesConfigInputConnectionListFactoryMetadata): { id: number; alias: string } => {
      return {
        id,
        alias: '',
      };
    },
    placeholderFactory: ({ id, aggregation, mainValue }: Record<string, unknown>): string => {
      return createAliasForAggregation(
        {},
        id as number,
        mainValue as string,
        aggregation as string,
      );
    },
    listLabelPaths: ['alias', 'aggregation'],
    childKnobFactory: (index, model, fields) =>
      conditionalField(
        observedField(
          selectDataValueField(
            'Y Axis',
            [
              index === 0 ? 'content.data.aggregation' : `${path}.${index}.aggregation`,
              index === 0 ? 'content.data.mainValue' : `${path}.${index}.mainValue`,
            ],
            ((): (string | SelectOption<ChartAggregateFunctionType, string>[])[] => {
              const usedAggregation = getValueByPath(model, 'content.data.aggregation');
              const fieldsFromFieldFactoryAfterMainAggregations = fields?.filter((v) => 'id' in v);
              return [
                fieldsFromFieldFactoryAfterMainAggregations?.length === 0
                  ? dataSourceOptions
                  : usedAggregation === 'FIELD VALUE'
                    ? dataSourceOptions.filter((a) => a.value === 'FIELD VALUE')
                    : dataSourceOptions.filter((a) => a.value !== 'FIELD VALUE'),
                'data.?key:value.dataSource',
              ];
            })(),
            // [index > 0 ? aggregations.filter(a => a.value !== 'FIELD VALUE') : aggregations, 'data.?key:value.dataSource'],
            {
              dataBindingsMode: 'first-dataset',
              allowSubSelection: true,
              filterColumnsType: ['Formula', 'Number'],
              emptyListPlaceholder: 'No numeric fields',
              // prevent Selecting Column For Count
              allowOpenStepsPredicate: (
                option: SchemaColumnSelectOption,
                index: number,
              ): boolean => {
                if (option.value === 'COUNT' && index === 0) {
                  return false;
                }
                return true;
              },
              backBtnLabel: 'Back to Aggregation Methods',
              stepsTagViewUse: [true],
              // is Compute Node For Chart
              nodePredicate: (node: WorkflowNode): boolean => {
                return (
                  !!findWorkflowNode([node], isAggregateNode, 'aggregate', 'chart-') ||
                  !!findWorkflowNode([node], isComputeNode, undefined, 'chart-')
                );
              },
              placeholder: 'Select',
              hideLabel: true,
              hideLabelSpace: true,
            },
          ),
          (model: ChartPropsModel, value: string) => {
            const result: Record<string, string> = {};
            if (index === 0) {
              // we need to assign those values to main aggregation and mainValue
              if (value && dataSourceOptions.find((v) => v.value === value)) {
                result['content.data.aggregation'] = value;
                if (value === 'COUNT') {
                  result[`content.data.mainValue`] = '';
                }
              }
              if (value && !dataSourceOptions.find((v) => v.value === value)) {
                result['content.data.mainValue'] = value;
              }
            } else if (value === 'COUNT') {
              result[`${path}.${index}.mainValue`] = '';
            }
            return result;
          },
        ),
        '',
        () => false,
      ),
    fieldActionFactory: (_, index, items) =>
      !additionalInputOptions?.preventListAction && items.length > 1,
    fieldRemoveFactory: (_, index) => index !== 0,
  },
});
const listChartCustomIcons = (
  path: string,
  label: string,
): PropertiesConfigItemDefinition<'collectionList'> => ({
  path,
  inputType: 'collectionList',
  label,
  inputOptions: {
    disableDrag: false,
    hideAddButton: true,
    actionsAlwaysVisible: true,
    fieldAction: 'CHARTS_EDIT_LABEL',
    fieldPaneTitle: '',
    itemTemplate: 'selectColoredIcon',
    fieldConfig: () => [subsection(`data.?key:value.enabled`, '', [], { hideLabel: true })],
    fieldFactory: () => ({}),
    listLabelPaths: ['label', 'icon', 'color'],
    childKnobFactory: (index) =>
      textInput(`${path}.${index}`, 'Label', { hideLabel: true, readonly: true }),
    fieldActionFactory: () => false,
    fieldRemoveFactory: () => true,
  },
});

const listFiltersField = (
  path: string,
  label: string,
  options?: Partial<PropertiesConfigInputOptionsCollectionList>,
): PropertiesConfigItemDefinition<'collectionList'> => ({
  path,
  inputType: 'collectionList',
  label,
  inputOptions: {
    fieldAction: 'COLLECTION_EDIT_FILTERS',
    fieldPaneTitle: 'Add Filter',
    fieldConfig: (index) => [
      subsection(
        `${path}.${index}`,
        '',
        [
          observedField(
            selectFieldFromDataBinding(`${path}.${index}.filterField`, 'Filter by', 'value', {
              dataBindingsMode: 'first-dataset',
            }),
            (model, columnIdOrName) => {
              const previousFieldValue =
                getValueByPath<string | undefined>(model, `${path}.${index}.filterField`) ??
                undefined;
              const placeholderValue = getValueByPath(model, `${path}.${index}.placeholder`);

              const schemaService = inject(SchemaService);
              const newFieldName = schemaService.getColumnName(columnIdOrName);
              const oldFieldName = schemaService.getColumnName(previousFieldValue);

              const dataProps: CanvasElementDataProperties[] = model.data;

              // Get previous column info
              const prevColumn = previousFieldValue
                ? getFirstDatasetColumnFromDataProps(dataProps, 'value', previousFieldValue)
                : undefined;

              // Get latest column info
              const newColumn = getFirstDatasetColumnFromDataProps(
                dataProps,
                'value',
                columnIdOrName,
              );
              const changes: Record<string, unknown> = {};

              if (prevColumn?.dataType !== newColumn?.dataType && arePropsWithWrapperRefs(model)) {
                changes[`${path}.${index}.__fieldTypeChanged`] = true;
                // The control gets regenerated with the column's default type
                changes[`${path}.${index}.controlType`] = undefined;
                changes[`${path}.${index}.toggleDefault`] = undefined;
              }
              const isColumnDateLike = (c: DatasetColumn | undefined): boolean => {
                return c?.dataType === 'Date' || c?.dataType === 'DateTime';
              };

              // Was the placeholder not changed by the user
              const isPlaceholderPristine =
                !placeholderValue ||
                (prevColumn?.dataType === 'Date'
                  ? FILTERS_DATE_OPERATOR_OPTIONS.some(
                      (option) => placeholderValue === option.label,
                    )
                  : prevColumn?.dataType === 'DateTime'
                    ? FILTERS_DATETIME_OPERATOR_OPTIONS.some(
                        (option) => placeholderValue === option.label,
                      )
                    : placeholderValue === oldFieldName);

              let placeholderOverride: string | undefined;

              // Apply the default operator
              let defaultOperator: ComparisonOperator | undefined;
              const previousOperator =
                getValueByPath<ComparisonOperator>(model, `${path}.${index}.operator`) ?? undefined;

              if (isColumnDateLike(newColumn)) {
                const options =
                  newColumn?.dataType === 'Date'
                    ? FILTERS_DATE_OPERATOR_OPTIONS
                    : FILTERS_DATETIME_OPERATOR_OPTIONS;
                // If used operator is available - keep it
                if (options.some((operator) => operator.value === previousOperator)) {
                  defaultOperator = previousOperator;
                }

                defaultOperator ??= ComparisonOperator.Gte; // Fallback to Start Date
                // Update placeholder if it was not modified by the user
                if (isPlaceholderPristine) {
                  placeholderOverride = options.find(
                    ({ value }) => value === defaultOperator,
                  )?.label;
                }
              } else if (newColumn?.dataType === 'Number') {
                // If used operator is available - keep it
                if (previousOperator && FILTERS_NUMBER_OPERATORS.includes(previousOperator)) {
                  defaultOperator = previousOperator;
                }
                defaultOperator ??= ComparisonOperator.Eq; // Fallback to Eq
              }

              changes[`${path}.${index}.operator`] = defaultOperator;

              // Update placeholder if it was not modified by the user
              if (
                placeholderOverride === undefined &&
                newFieldName &&
                (!placeholderValue || isPlaceholderPristine)
              ) {
                placeholderOverride = newFieldName;
              }

              if (placeholderOverride !== undefined) {
                changes[`${path}.${index}.placeholder`] = placeholderOverride;
              }

              return changes;
            },
          ),
          conditionalField(
            // filterOperaterType change can mean that displayed field type also changed.
            observedField(
              radiosField(
                `${path}.${index}.filterOperatorType`,
                '',
                [
                  { value: 'Setup', label: 'Setup' },
                  { value: 'Predefined', label: 'Predefined' },
                ],
                { defaultValue: 'Setup', hideLabel: true, leftIndent: false },
              ),
              (model, filterOperatorType) => {
                const previousFilterOperatorType =
                  getValueByPath<string | undefined>(
                    model,
                    `${path}.${index}.filterOperatorType`,
                  ) ?? undefined;
                const changes: Record<string, unknown> = {};
                if (
                  previousFilterOperatorType !== filterOperatorType &&
                  arePropsWithWrapperRefs(model)
                ) {
                  changes[`${path}.${index}.__fieldTypeChanged`] = true;
                  if (filterOperatorType === 'Predefined') {
                    changes[`${path}.${index}.operator`] = undefined;
                    changes[`${path}.${index}.placeholder`] = 'Filter Date';
                  }
                  if (filterOperatorType === 'Setup') {
                    const operator = ComparisonOperator.Gte; // Start Date;
                    const placeholder = FILTERS_DATE_OPERATOR_OPTIONS.find(
                      ({ value }) => value === operator,
                    )?.label;
                    changes[`${path}.${index}.operator`] = operator;
                    changes[`${path}.${index}.placeholder`] = placeholder;
                  }
                }
                return changes;
              },
            ),
            `${path}.${index}`,
            (filter: PropertiesConfigInputListFiltersModel, model) => {
              const columnIdOrName = filter?.filterField;
              if (!columnIdOrName) {
                return true;
              }
              const columnDataType = getFirstDatasetColumnFromDataProps(
                model.data as CanvasElementDataProperties[],
                'value',
                columnIdOrName,
              )?.dataType;
              return columnDataType !== 'Date' && columnDataType !== 'DateTime';
            },
          ),

          // "Setup" Date and DateTime operators
          ...conditionalFields(
            [
              observedField(
                selectFieldFromApi(
                  `$$filterField.${index}.controlType`,
                  'Component',
                  'LIST_CONTROLS_FOR_FILTER',
                  {
                    showSelection: true,
                    allowNone: false,
                    modelData: `${path}.${index}`,
                  },
                ),
                (props, value, _, elementId) => {
                  const fieldId =
                    getValueByPath<number>(props, `content.searchAndFilters.filters.${index}.id`) ??
                    undefined;
                  inject(ISearchAndFiltersWrapperService).updateFilterElementType(
                    elementId,
                    fieldId,
                    value,
                  );
                  // Mirror the (virtual) control type into the filter model,
                  // so the fields below can conditionally show/hide on it
                  return { [`${path}.${index}.controlType`]: value };
                },
              ),
              listFiltersDateLikeCondition(path, index, 'Date', FILTERS_DATE_OPERATOR_OPTIONS),
              listFiltersDateLikeCondition(
                path,
                index,
                'DateTime',
                FILTERS_DATETIME_OPERATOR_OPTIONS,
              ),
            ],
            `${path}.${index}`,
            (field) => {
              return !field?.filterField || field?.filterOperatorType === 'Predefined';
            },
          ),

          // "Predefined" Date and DateTime operators
          ...conditionalFields(
            [
              listPredefinedFiltersDateLikeCondition(
                path,
                index,
                'Date',
                FILTERS_DATE_AND_TIME_PREDEFINED_OPERATOR_OPTIONS,
              ),
              listPredefinedFiltersDateLikeCondition(
                path,
                index,
                'DateTime',
                FILTERS_DATE_AND_TIME_PREDEFINED_OPERATOR_OPTIONS,
              ),
            ],
            `${path}.${index}`,
            (field) => {
              return !field?.filterField || field?.filterOperatorType !== 'Predefined';
            },
          ),

          // Number operator - visible when the column is a number
          conditionalField(
            selectFieldFromList(
              `${path}.${index}.operator`,
              'Operator',
              FILTERS_NUMBER_OPERATORS.map((operator) => ({
                label: DEFAULT_OPERATOR_NAMES[operator],
                value: operator,
              })),
              { showSelection: true, allowNone: false },
            ),
            `${path}.${index}`,
            (filter: PropertiesConfigInputListFiltersModel, model) => {
              const columnIdOrName = filter?.filterField;
              if (!columnIdOrName) {
                return true;
              }

              const columnDataType = getFirstDatasetColumnFromDataProps(
                model.data as CanvasElementDataProperties[],
                'value',
                columnIdOrName,
              )?.dataType;
              return columnDataType !== 'Number';
            },
          ),
          conditionalField(
            textInput(`${path}.${index}.placeholder`, 'Placeholder', { placeholder: 'Filter' }),
            `${path}.${index}`,
            (filter: PropertiesConfigInputListFiltersModel) =>
              filter?.controlType === 'toggleButton',
          ),

          // Initial state of the 2-state (toggle button) filter control
          conditionalField(
            selectFieldFromList(
              `${path}.${index}.toggleDefault`,
              'Default',
              [
                { value: 'disabled', label: 'Disabled' },
                { value: 'enabled', label: 'Enabled' },
              ],
              { showSelection: true, allowNone: false, defaultValue: 'disabled' },
            ),
            `${path}.${index}`,
            (filter: PropertiesConfigInputListFiltersModel) =>
              filter?.controlType !== 'toggleButton',
          ),
        ],
        {
          hideLabel: true,
          dialogWidth: '330px',
        },
      ),
    ],
    fieldFactory: ({ id }: PropertiesConfigInputConnectionListFactoryMetadata) => ({
      id,
      placeholder: '',
    }),
    listLabelPaths: ['placeholder', 'filterField'],
    isItemVisible: (item: PropertiesConfigInputListFiltersModel) => !!item.filterField,
    verticalPadding: true,
    ...options,
  },
});

function listFiltersDateLikeCondition(
  path: string,
  index: number,
  colDataType: ColumnDataType | undefined,
  options: SelectOption<ComparisonOperator | PredefinedDateTimeOperator, string>[],
): PropertiesConfigItemDefinition {
  return conditionalField(
    observedField(
      selectFieldFromList(`${path}.${index}.operator`, 'Operator', options, {
        showSelection: true,
        allowNone: false,
      }),
      (model, operator) => {
        const previousOperatorValue = getValueByPath<ComparisonOperator>(
          model,
          `${path}.${index}.operator`,
        );
        const placeholderValue = getValueByPath<string>(model, `${path}.${index}.placeholder`);
        const previousOperatorLabel = options.find(
          ({ value }) => value === previousOperatorValue,
        )?.label;
        const isPlaceholderPristine =
          !placeholderValue || placeholderValue === previousOperatorLabel;

        // Update placeholder if it was not modified by the user
        if (operator && isPlaceholderPristine) {
          return {
            [`${path}.${index}.placeholder`]: options.find(({ value }) => value === operator)
              ?.label,
          };
        }

        return {};
      },
    ),
    `${path}.${index}`,
    (filter: PropertiesConfigInputListFiltersModel, model) => {
      const columnIdOrName = filter?.filterField;
      if (!columnIdOrName) {
        return true;
      }

      const columnDataType = getFirstDatasetColumnFromDataProps(
        model.data as CanvasElementDataProperties[],
        'value',
        columnIdOrName,
      )?.dataType;
      return columnDataType !== colDataType;
    },
  );
}

function listPredefinedFiltersDateLikeCondition(
  path: string,
  index: number,
  colDataType: ColumnDataType | undefined,
  options: SelectOption<ComparisonOperator | PredefinedDateTimeOperator, string>[],
): PropertiesConfigItemDefinition {
  return conditionalField(
    observedField(
      selectFieldFromList(`${path}.${index}.predefinedOperators`, 'Available filters', options, {
        allowMultiSelect: true,
        allowNone: false,
        showSelectedItemsCount: true,
        showSelection: true,
      }),
      (model, operator) => {
        const previousOperatorValue = getValueByPath<ComparisonOperator>(
          model,
          `${path}.${index}.predefinedOperators`,
        );
        const placeholderValue = getValueByPath<string>(model, `${path}.${index}.placeholder`);
        const previousOperatorLabel = options.find(
          ({ value }) => value === previousOperatorValue,
        )?.label;
        const isPlaceholderPristine =
          !placeholderValue || placeholderValue === previousOperatorLabel;

        // Update placeholder if it was not modified by the user
        if (operator && isPlaceholderPristine) {
          return {
            [`${path}.${index}.placeholder`]: 'Filter Date',
          };
        }

        return {};
      },
    ),
    `${path}.${index}`,
    (filter: PropertiesConfigInputListFiltersModel, model) => {
      const columnIdOrName = filter?.filterField;
      if (!columnIdOrName) {
        return true;
      }

      const columnDataType = getFirstDatasetColumnFromDataProps(
        model.data as CanvasElementDataProperties[],
        'value',
        columnIdOrName,
      )?.dataType;
      return columnDataType !== colDataType;
    },
  );
}

const listItemValues = (
  path: string,
  label: string,
): PropertiesConfigItemDefinition<'collectionList'> => ({
  path,
  inputType: 'collectionList',
  label,
  inputOptions: {
    fieldAction: 'ELEMENT_OPTIONS_LIST',
    fieldPaneTitle: 'List Value',
    fieldConfig: (index: number) => [
      subsection(
        `${path}.${index}`,
        '',
        [textInput(`${path}.${index}`, 'Value', { placeholder: 'Value' })],
        { hideLabel: true },
      ),
    ],
    itemTemplate: 'image',
    fieldFactory: () => 'New option' as any,
    listLabelPaths: [],
  } as PropertiesConfigInputOptionsCollectionList,
});

const tileWithCheckboxItemValues = (
  basePath: string,
  label: string,
  options?: { withSubText?: boolean },
): PropertiesConfigItemDefinition<'collectionList'> => ({
  path: basePath + '.values',
  inputType: 'collectionList',
  label,
  inputOptions: {
    itemTemplate: 'image',
    fieldAction: 'ELEMENT_OPTIONS_LIST',
    fieldPaneTitle: 'List Value for tile selector',
    fieldConfig: (index: number) => [
      subsection(
        `${basePath}.values.${index}`,
        '',
        [
          textInput(`${basePath}.values.${index}`, 'Value', { placeholder: 'Value' }),
          ...(options?.withSubText
            ? [
                conditionalField(
                  textInput(`${basePath}.subheading.${index}`, 'Sub text', {
                    placeholder: 'Sub text',
                  }),
                  'content.mode',
                  (mode) => mode !== 'box',
                ),
              ]
            : []),
          ...conditionalFields(
            [
              separator('Image', { hideTopSeparator: true, useNormalFontWeight: true }),
              uploadField(`${basePath}.images.${index}.src`, 'Type URL or upload image'),
            ],
            'content.mode',
            (mode) => mode !== 'image',
          ),
          conditionalField(
            iconField(`${basePath}.images.${index}.icon`),
            'content.mode',
            (mode) => mode !== 'box',
          ),
        ],
        { hideLabel: true },
      ),
    ],
    fieldFactory: () => {
      return 'New option' as any;
    },
    listLabelPaths: [],
  } as PropertiesConfigInputOptionsCollectionList,
});

const radioListItemValues = (
  path: string,
  imagesPath: string,
  label: string,
): PropertiesConfigItemDefinition<'collectionList'> => ({
  path,
  inputType: 'collectionList',
  label,
  inputOptions: {
    itemTemplate: 'image',
    fieldAction: 'ELEMENT_OPTIONS_LIST',
    fieldPaneTitle: 'List Value for tile selector',
    fieldConfig: (index: number) => [
      subsection(
        `${path}.${index}`,
        '',
        [
          textInput(`${path}.${index}`, 'Value', { placeholder: 'Value' }),
          ...conditionalFields(
            [
              separator('Image', { hideTopSeparator: true, useNormalFontWeight: true }),
              uploadField(`${imagesPath}.${index}.src`, 'Type URL or upload image'),
            ],
            'content.mode',
            (mode) => mode !== 'image',
          ),
          conditionalField(
            iconField(`${imagesPath}.${index}.icon`),
            'content.mode',
            (mode) => mode !== 'box',
          ),
        ],
        { hideLabel: true },
      ),
    ],
    fieldFactory: () => {
      return 'New option' as any;
    },
    listLabelPaths: [],
  } as PropertiesConfigInputOptionsCollectionList,
});

function actionConfig(
  path: string,
  options?: {
    label?: string;
    hideWhen?: HideFieldWhenFn;
  } & PropertiesConfigInputOptionsActionConfig,
): PropertiesConfigItemDefinition<'actionConfig'> {
  return {
    label: options?.label ?? 'Action',
    inputType: 'actionConfig',
    path,
    inputOptions: {
      ...options,
    },
  };
}

function aiTriggerField(
  path: string,
  options?: {
    actionGenerationType?: ActionKind;
    hideWhen?: HideFieldWhenFn;
  },
): PropertiesConfigItemDefinition<'aiTrigger'> {
  return {
    label: '',
    inputType: 'aiTrigger',
    path,
    inputOptions: {
      actionGenerationType: options?.actionGenerationType,
      hideWhen: options?.hideWhen,
    },
  };
}

//#endregion

const emptyStateFields = (options: {
  label?: string;
  messagePath: string;
  modPrefix?: ModPrefix;
  visibilityPath?: string;
  hideLabel?: boolean;
}): PropertiesConfigItemDefinition[] => {
  const { label, modPrefix = '', visibilityPath, messagePath, hideLabel } = options;

  const stateField = observedField<'emptyState'>(
    {
      path: visibilityPath || 'content.visible',
      label: label || 'When empty',
      inputType: 'emptyState',
      inputOptions: {
        hideLabel,
      },
    },
    updateVisibilityDataSourceOnValueChange(modPrefix, visibilityPath),
  );

  const messageField = conditionalField<'textArea'>(
    {
      path: messagePath,
      label: '',
      inputType: 'textArea',
      inputOptions: {
        rows: 3,
        disableResize: true,
        hideLabel,
      },
    },
    stateField.path,
    (model: PropertiesConfigInputVisibilityModel | undefined) => {
      return !!model?.internalStates?.includes('hideWhenNoData');
    },
  );

  return [stateField, messageField];
};

export const propsConfigHelper = {
  initConfig,
  section,
  subsections: {
    calendarDaysStyleSubsection,
    columnSizeSubsection,
    complexSearchAndFiltersSubsection,
    dataSubsection,
    elementBackgroundSubsection,
    elementFontSubsection,
    elementSizeSubsection,
    elementUIPartStyleSubsection,
    labelContentSubsection,
    labelStyleSubsection,
    labelSubsection,
    searchAndFiltersSubsection,
    styleSheetSubsection,
    subsection,
    titleContentSubsection,
    titleStyleSubsection,
    triggerSubsection,
    visibilityColumnSubsection,
    visibilitySubsection,
  },
  fields: {
    actionConfig,
    actionsDisplayModeField,
    alignField,
    barcodeScannerFormatField,
    buttonActionField,
    buttonVisibleField,
    checksField,
    colorField,
    colorSimpleField,
    columnSizeField,
    containerItemActionsField,
    containerRowClickActionsField,
    contentAlignField,
    contentOverflowField,
    coordsField,
    customTemplateField,
    currencyConditionalField,
    dateFormatField,
    dateTimeFormatField,
    directionField,
    durationFormatField,
    emptyStateFields,
    fontFamilyAndWeightFields,
    fontStyleField,
    genericSizeField,
    horizontalAlignField,
    horizontalPositionField,
    iconField,
    iconPositionField,
    imageAlignField,
    imageBehaviorField,
    imagePropertiesFields,
    inputTitlePositionField,
    layoutField,
    linkFields,
    listChartCustomIcons,
    listChartFields,
    listChatIntroMessagesFields,
    listConditionalFormattingField,
    listCustomFieldsField,
    listField,
    listFieldsField,
    listFiltersField,
    listItemActionsField,
    listItemValues,
    listLayoutField,
    listTableGroupByFields,
    navMenuItemsField,
    numberPrecisionField,
    positionField,
    radiosField,
    radiusField,
    ratioField,
    remoteTextField,
    resizeBehaviorField,
    selectDataSourceField,
    selectDataSourceWithAlert,
    selectDataValueField,
    selectFieldFromApi,
    selectFieldFromDataBinding,
    selectFieldFromFontWeights,
    selectFieldFromFormActions,
    selectFieldFromFormFonts,
    selectFieldFromFormPages,
    selectFieldFromImageList,
    selectFieldFromList,
    selectFieldFromModel,
    selectFieldFromSchema,
    selectFieldFromUsersColumns,
    sizeField,
    sliderField,
    sortField,
    spacingAndAlignContainerField,
    spacingAndAlignField,
    subConfigField,
    tagsColorPaletteField,
    textAreaField,
    textFieldFormatField,
    textInfo,
    textInput,
    textInputFormatField,
    tileWithCheckboxItemValues,
    tileWithImagesListItemValues: radioListItemValues,
    timeRangeField,
    titleField,
    titlePositionField,
    toggleArrayField,
    toggleField,
    unifiedActionsField,
    uploadField,
    viewportVisibilityField,
    visibilityField,
    visualEffectField,
    wysiwygAreaField,
  },
  root: {
    elementHeightField,
    elementWidthField,
  },
  utils: {
    adornField,
    columnDataTypeFilter,
    columnFiltersCombination,
    columnFormatFilter,
    columnMultipleFilter,
    conditionalField,
    conditionalFields,
    dataSourcedField,
    disabledField,
    disabledFields,
    elementMetaDisplayValue,
    lockOverride,
    observedField,
    separatedField,
    separator,
  },
};
